import React, { Component } from 'react';
import {
  inject,
  observer,
} from 'mobx-react';
import {
  ErrorMessage,
  Field,
  FieldArray,
  Form,
  Formik,
} from 'formik';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  Row,
  Panel,
  InputGroup,
} from 'react-bootstrap';
import * as yup from 'yup';
import SideBar from '../../components/SideBar/SideBar';
import LoadingButton from '../../components/LoadingButton/LoadingButton';
import cx from 'classnames';
import { toNumber } from '../../utils/numbers';
import logger from '../../utils/logger';
import {
  DragDropContext,
  Draggable,
  Droppable,
} from 'react-beautiful-dnd';
import shortid from 'shortid';
import {
  MaterialType,
  BayList,
} from '../../constants/MaterialModels';
import moment from 'moment-timezone';
import { withTranslation } from 'react-i18next';
import convertUnit from '../../utils/unit-converter';
import { Dropdown } from 'semantic-ui-react';
import 'semantic-ui-css/components/dropdown.min.css';
import 'semantic-ui-css/components/transition.min.css';
import 'semantic-ui-css/components/reset.min.css';
import './BatchForm.css';
import {
  Measurement,
  RunMode,
  WaterAdjustBy,
} from '../../constants/Configuration';
import { reaction } from 'mobx';
import { showConfirmModal } from '../../modules/modals/confirmService';
import { OrderOrigin } from '../../constants/OrderOrigin';
import { randomId } from '../../utils/random-id';
import {
  getAllowedWater,
  getTotalWater,
  getWCRatio,
  isAllowedWaterExceeded,
} from './utils/water-utils';
import {
  adjustMixDesign,
  isMeteredWaterInTheMix,
  calculateMixDesign,
  checkMixDesignOrder,
  getBinPercent,
  getMoisture,
  checkMixDesignQuantityAndTrim,
  isWeighedWaterInTheMix,
  validateNegativeTrim,
  getQuantityTypeUnitForAdjustedMix,
} from './utils/mix-design-utils';
import { isWaterOrSecondWater } from './utils/material-utils';
import { canBatch } from '../../stores/batch/can-batch';
import { IceUnits } from '../../constants/IceUnit';
import Tabs from 'react-bootstrap/lib/Tabs';
import Tab from 'react-bootstrap/lib/Tab';
import BatchFormLogs from '../../components/BatchFormLogs/BatchFormLogs';
import { savePreviousValues } from './utils/previous-values-manager';
import BatchFormExtraTools from './components/BatchFormExtraTools';
import { DEFAULT_QUANTITY_TO_BE_BATCHED } from '../../constants/BatchDefaults';
import { PERMISSION } from '../../modules/newAuth/model/User';
import ManualAddList from './components/ManualAddList';
import { ConfirmWithInitials } from '../../components/Confirm/ConfirmWithInitials';
import { BatchEventType } from '../../constants/BatchEventType';

class BatchForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      errorsToOverride: [],
      error: '',
      errors: [],
      warnings: '',
      mixDifferWarnings: [],
      info: [],
      initialValues: this.getInitialValues(),
      enableOfflineBatch: false,
      autoAdjusted: false,
      overrideErrors: false,
      forceBatchInitials: null,
      forceIndicatorErrorInitials: null,
    };
  }

  componentDidMount() {
    const {
      mixStore,
      materialStore,
      dischargeStore,
      ticketStore,
      slumpTableStore,
      manualAddStore,
    } = this.props;

    if (!mixStore.allMixes.length) {
      mixStore.load();
    }

    if (!materialStore.unit.length) {
      materialStore.loadTypes();
    }

    if (!dischargeStore.sequences.length) {
      dischargeStore.getSequences();
    }

    if (!dischargeStore.mixCharge.length || !dischargeStore.truckCharge.length) {
      dischargeStore.loadCharge();
    }

    if (!ticketStore.templates.length) {
      ticketStore.loadTemplates();
    }

    if (!slumpTableStore.slumpTables.length) {
      slumpTableStore.fetchSlumpTables();
    }

    if (!manualAddStore.manualAddList.length) {
      manualAddStore.loadManualAddList();
    }

    this.checkOrderIdChange();
  }

  componentDidUpdate(prevProps) {
    const { location, sidebarStore } = this.props;

    if (location !== prevProps.location) {
      if (!this.isBatchPage) {
        sidebarStore.onToggle(null);
      }
    }
  }

  setInitialState() {
    this.setState({
      errorsToOverride: [],
      error: '',
      errors: [],
      warnings: [],
      info: [],
      mixDifferWarnings: [],
      enableOfflineBatch: false,
      autoAdjusted: false,
      showBins: false,
    });
  }

  setInitialValues(orderId) {
    const { batchStore } = this.props;

    if (batchStore.replayMode) {
      this.setState({
        initialValues: this.getDataFromReplay(orderId),
      });
    } else {
      this.setState({
        initialValues: orderId ? this.getBatchDataFromOrder(orderId) : this.getInitialValues(),
      });
    }
  }

  getDataFromReplay(batchTicketId) {
    const { batchStore } = this.props;
    const load = batchStore.loadHistory.find(v => v.running?.length);
    const data = load?.running?.length ? load.running.find(v => batchTicketId === v.id) : null;
    return data || this.getInitialValues();
  }

  checkOrderIdChange() {
    reaction(() => this.props.batchStore.orderId,
      (orderId) => {
        this.setInitialValues(orderId);
      });
  }

  get isBatchPage() {
    const { pathname } = this.props.location;
    return pathname === '/' || String(pathname).includes('index');
  }

  get validationSchema() {
    const { maximumBatchSize = 0, maximumDropSize = 0 } = this.props.settingsStore.configuration;

    return yup.object().shape({
      mixId: yup.string().required('Required!'),
      customerId: yup.string().required('Required!'),
      quantityToBeBatched: yup.number().min(0.1, 'Minimum: 0.1').required('Required!'),
      batchSize: yup.number().min(0, 'Minimum: 0').max(maximumBatchSize, `Maximum: ${maximumBatchSize}`),
      allDrops: yup.number().min(0, 'Minimum: 0').max(maximumBatchSize, `Maximum: ${maximumBatchSize}`),
      dropSize: yup.number().min(0, 'Minimum: 0').max(maximumDropSize, `Maximum: ${maximumDropSize}`),
      mixQuantityInTruck: yup.number().min(0, 'Minimum: 0'),
      ice: yup.number().min(0, 'Minimum: 0'),
      waterInTruck: yup.number().min(0, 'Minimum: 0'),
      dischargeSequenceId: yup.string().required('Required!'),
      secondWater: yup.number().min(-100, 'Minimum: -100').max(100, 'Maximum: 100'),
      mixDesign: yup.array().of(yup.object({
        constituentItemId: yup.string().required('Required'),
        trim: yup.number().min(-30000, 'Minimum: -30000').max(30000, 'Maximum: 30000'),
        moisture: yup.number().min(-100, 'Minimum: -100').max(100, 'Maximum: 100'),
      })),
    });
  }

  get driverOptions() {
    const { mixStore, scheduleStore } = this.props;
    return mixStore.getOptions(scheduleStore.drivers, {
      key: 'id',
      text: ['code', 'name'],
      value: 'id',
      className: 'driver-option',
      subheader: 'code',
      description: 'name',
    });
  }

  get truckOptions() {
    const { mixStore, scheduleStore } = this.props;
    return mixStore.getOptions(scheduleStore.trucks, {
      key: 'id',
      text: ['code', 'description'],
      value: ['id'],
      subheader: 'code',
      description: 'description',
    });
  }

  get projectOptions() {
    const { mixStore, scheduleStore } = this.props;
    return mixStore.getOptions(scheduleStore.projects, {
      key: 'id',
      text: ['code', 'name'],
      value: ['id'],
      subheader: 'code',
      description: 'name',
    });
  }

  get customerOptions() {
    const { mixStore, scheduleStore } = this.props;

    return mixStore.getOptions(scheduleStore.customers, {
      key: 'id',
      text: ['code', 'name'],
      value: ['id'],
      subheader: 'code',
      description: 'name',
    });
  }

  isAutoMoisture(materialId) {
    const { materialStore } = this.props;
    const md = materialStore.materialsWithDetails.find(v => v.itemId === Number(materialId)) || {};
    return !!md.autoMoisture;
  };

  isLiveMoisture(materialId) {
    const { materialStore } = this.props;
    const md = materialStore.materialsWithDetails.find(v => v.itemId === Number(materialId)) || {};
    return !!md.liveMoisture;
  };

  getInitialValues = () => {
    const { settingsStore, devicesStore } = this.props;
    const { configuration } = settingsStore;

    return {
      quantityToBeBatched: DEFAULT_QUANTITY_TO_BE_BATCHED,
      mixId: '',
      mixDescription: '',
      truckId: '',
      truckCode: '',
      driverId: '',
      driverCode: '',
      driverName: '',
      unitId: '',
      projectId: '',
      projectName: '',
      projectCode: '',
      customerId: '',
      customerName: '',
      customerCode: '',
      slump: 0,
      originalSlump: 0,
      slumpTableId: configuration?.slumpTableId,
      dispatchOrderId: '',
      dispatchOrderCode: '',
      code: '',
      mixQuantityInTruck: 0,
      mixInTruck: '',
      secondWater: 0,
      waterInTruck: 0,
      allDrops: 1,
      ice: 0,
      iceUnit: configuration && IceUnits[configuration.measurement][0].key,
      batchSize: 1,
      dropSize: 1,
      printWithoutWeights: configuration?.printWithoutWeights ?? false,
      printWithoutWeightsAtEnd: configuration?.printWithoutWeightsAtEnd ?? false,
      printWithWeights: configuration?.printWithWeights ?? true,
      printOnlyWeights: configuration?.printOnlyWeights ?? false,
      order: null,
      mix: null,
      mixDesign: [],
      extra: [],
      adjustedMixDesignId: null,
      adjustedMixes: [],
      dischargeSequenceId: configuration?.defaultDischargeSequenceId ?? '',
      waterUnit: '',
      waterAdjust: 0,
      mixerId: devicesStore.getNextMixer(),
      overrideErrors: false,
      useBay: configuration.useBay ?? false,
      bay: 0,
      origin: OrderOrigin.Local,
      lotBlockNumber: '',
      totalQuantityTax: 0,
      totalQuantityPrice: 0,
      autoMoistureDeduction: 0,
    };
  };

  hasMixDesignDuplicates(mixDesign = []) {
    const md = mixDesign.map(v => v.constituentItemId);
    const unique = Array.from(new Set(md));

    return unique.length !== md.length;
  }

  addExternalData(order) {
    const { scheduleStore } = this.props;

    if (order.driverCode && !order.driverId) {
      const d = scheduleStore.drivers.find(v => v.code === order.driverCode);

      if (!d) {
        const driver = {
          id: randomId(),
          code: order.driverCode,
          name: order.driverName || '',
        };

        scheduleStore.drivers.push(driver);
        order.driverId = driver.id;

      } else {
        order.driverId = d.id;
      }
    }

    if (order.truckCode && !order.truckId) {
      const d = scheduleStore.trucks.find(v => v.code === order.truckCode);

      if (!d) {
        const truck = {
          id: randomId(),
          code: order.truckCode,
          name: '',
          description: '',
        };

        scheduleStore.trucks.push(truck);
        order.truckId = truck.id;

      } else {
        order.truckId = d.id;
      }
    }

    if (order.customerCode && !order.customerId) {
      const d = scheduleStore.customers.find(v => v.code === order.customerCode);

      if (!d) {
        const customer = {
          id: randomId(),
          code: order.customerCode,
          name: order.customerName || '',
          description: '',
        };

        scheduleStore.customers.push(customer);
        order.customerId = customer.id;

      } else {
        order.customerId = d.id;
      }
    }

  }

  getSlumpDesignFromMix(mixId) {
    const { mixStore } = this.props;
    const mix = mixStore.getOnlineMixById(Number(mixId));
    return mix?.slump || 0;
  }

  getSlumpTableIdFromMix(mixId) {
    const { mixStore } = this.props;
    const mix = mixStore.getOnlineMixById(Number(mixId));
    return mix?.slumpTableId;
  }

  getItemsToBeAddedManually(extraProducts) {
    const { manualAddStore} = this.props;
    const { manualAddList } = manualAddStore;
    let itemsToAdd = [];

    extraProducts.forEach(v => {
      const item = manualAddList.find(x => x.extraProductItemCode === v.itemCode);
      if (item?.constituentItems?.length && Array.isArray(item?.constituentItems)) {
        itemsToAdd = itemsToAdd.concat(item?.constituentItems);
      }
    });

    return itemsToAdd;
  }

  showItemsToBeManuallyAdded(itemsToAdd, orderMixQuantity) {
    const { t } = this.props;

    if (itemsToAdd?.length) {
      return showConfirmModal({
        title: `${t('Manual Add')}`,
        message: (<ManualAddList itemsToAdd={itemsToAdd} orderMixQuantity={orderMixQuantity} />),
        okLabel: 'OK',
        cancelLabel: null,
        enableEscape: false,
      }).catch(_ => {});
    }
  }

  getBatchDataFromOrder = (orderId) => {
    const { scheduleStore, mixStore, batchStore, materialStore, settingsStore } = this.props;
    const { configuration } = settingsStore;
    let order = scheduleStore.getScheduleById(orderId) || scheduleStore.getLocalOrderById(orderId);
    order = Object.assign({}, order);
    let mix = order.products ? order.products.find(v => v.isMix) : null;
    const extra = order.products ? order.products.filter(v => !v.isMix) : [];
    const itemsToBeManuallyAdded = this.getItemsToBeAddedManually(extra);
    const mixQuantityInTruck = mix ? (mix.quantityInTruck || 0) : 0;
    let mixDesign = [];
    let dispatchMix;

    if (mix) {
      if (order.origin === OrderOrigin.Local) {
        mixDesign = mixStore.getMixDesign(Number(mix.itemId));
      } else {
        if (order.origin === OrderOrigin.External) {
          if (!mix.itemId) {
            mix = mixStore.addExternalMix(mix);
          }
          this.addExternalData(order);
        }
        dispatchMix = scheduleStore.mixDesignForDispatchTickets[order.id];
        mixDesign = dispatchMix && dispatchMix.constituentItems
          ? dispatchMix.constituentItems
          : [];
      }
    }

    if (this.hasMixDesignDuplicates(mixDesign)) {
      batchStore.showWarning('Mix Design contains duplicate constituent!');
    }

    if (!materialStore.hasBinOnlyOneMaterialAssigned()) {
      batchStore.showWarning('One of the bins is assigned and active on two different materials!');
    }

    const water = mixDesign.find(v => materialStore.isWater(v.constituentItemId));
    const waterUnit = water ? materialStore.getMaterialUnitSymbol(water.constituentItemId) : '';
    const orderQty = toNumber(order.orderMixQuantity, 2);
    const mixQty = mix ? toNumber(mix.quantity, 2) : 1;
    const qty = mixQty > orderQty ? orderQty : mixQty || 1;
    const maxDropSize = dispatchMix
      && dispatchMix.maximumLoadSize
      && dispatchMix.maximumLoadSize < batchStore.maximumDropSize
      ? dispatchMix.maximumLoadSize
      : mix ? this.getMaxDropSize(mix.itemId) : batchStore.maximumDropSize;
    const dropsData = batchStore.calculateDrops({
      quantityToBeBatched: qty,
      mixQuantityInTruck,
    }, maxDropSize);

    if (itemsToBeManuallyAdded?.length) {
      this.showItemsToBeManuallyAdded(itemsToBeManuallyAdded, dropsData.batchSize);
    }

    const mixId = mix ? mix.itemId : '';
    const truckId = order.truckId || '';
    const initialValues = this.getInitialValues();
    const dischargeSequenceId = this.getDischargeSequenceId({ ...initialValues, mixId, truckId, dropSize: dropsData.dropSize });
    const preSavedOrder = scheduleStore.getPreSavedOrder(orderId);
    const slump = order.mixSlump || dispatchMix?.targetSlump || 0;
    const originalSlump = this.getSlumpDesignFromMix(mix?.itemId);
    const slumpTableId = this.getSlumpTableIdFromMix(mix?.itemId) || configuration?.slumpTableId;

    let payload = {
      ...initialValues,
      dischargeSequenceId,
      id: order.id,
      origin: order.origin,
      order: order,
      quantityToBeBatched: qty,
      mixQuantityInTruck,
      batchSize: dropsData.batchSize,
      dropSize: dropsData.dropSize,
      allDrops: dropsData.allDrops,
      code: order.code || '',
      mixId,
      dispatchOrderId: order.dispatchOrderId || '',
      dispatchOrderCode: order.dispatchOrderCode || '',
      truckId,
      truckCode: order.truckCode || '',
      driverId: order.driverId || '',
      driverCode: order.driverCode || '',
      driverName: order.driverName || '',
      projectId: order.projectId || '',
      projectName: order.projectName || '',
      projectCode: order.projectCode || '',
      customerId: order.customerId || '',
      customerCode: order.customerCode || '',
      customerName: order.customerName || '',
      customerAddress: order.customerAddress || '',
      usage: order.usage || '',
      accountType: order.accountType || '',
      shipToAddress: order.shipToAddress || '',
      purchaseOrderNumber: order.purchaseOrderNumber || '',
      mixDescription: mix ? mix.itemDescription : '',
      unitId: mix ? mix.unitId : '',
      slump,
      originalSlump,
      slumpTableId,
      lotBlockNumber: order.lotBlockNumber || '',
      totalQuantityTax: order.totalQuantityTax,
      totalQuantityPrice: order.totalQuantityPrice,
      mix,
      extra,
      secondWater: this.getSecondWater(),
      ...adjustMixDesign(null, { mixDesign, extra, code: order.code, dispatchOrderId: order.dispatchOrderId }, mixId, this.props.batchStore.rootStore),
      itemsToBeManuallyAdded,
      waterUnit,
      waterAdjust: this.getWaterAdjust(order.dispatchOrderId, mixId),
      printWithoutWeights: order.printWithoutWeights || order.printWithWeights || order.printOnlyWeights || order.printWithoutWeightsAtEnd
        ? order.printWithoutWeights
        : configuration?.printWithoutWeights ?? false,
      printWithWeights: order.printWithoutWeights || order.printWithWeights || order.printOnlyWeights || order.printWithoutWeightsAtEnd
        ? order.printWithWeights
        : configuration?.printWithWeights ?? true,
      printOnlyWeights: order.printWithoutWeights || order.printWithWeights || order.printOnlyWeights || order.printWithoutWeightsAtEnd
        ? order.printOnlyWeights
        : configuration?.printOnlyWeights ?? false,
      printWithoutWeightsAtEnd: order.printWithoutWeights || order.printWithWeights || order.printOnlyWeights || order.printWithoutWeightsAtEnd
        ? order.printWithoutWeightsAtEnd
        : configuration?.printWithoutWeightsAtEnd ?? false,
      instructions: order.instructions || [],
      ...preSavedOrder,
    };

    if (payload.secondWater) {
      payload = this.addSecondWater(payload);
    }

    payload = this.adjustDrops(calculateMixDesign(payload, batchStore.rootStore));
    payload = {
      ...payload,
      originalPayload: { ...payload, mixDesign: [ ...payload.mixDesign ] },
    };

    return payload;
  };

  onAdjustMixDesignClick(values, setValues, count = 0) {
    const { batchStore } = this.props;

    this.setState({ autoAdjusted: true });

    if (count < 3) {
      setValues(calculateMixDesign(batchStore.adjustMixDesignToMixInTruck(values), batchStore.rootStore));

      setTimeout(() => {
        this.verify(values);
        this.onAdjustMixDesignClick(values, setValues, count+1);
      }, 0);
    }
  }

  adjustDrops(values, setValues) {
    const { batchStore, settingsStore } = this.props;
    let payload = this.verify(values, false);
    const verification = canBatch(payload || values, settingsStore.rootStore);
    let info = [];

    if (verification.errors.length) {
      verification.errors.forEach(v => {
        if (v.name === 'scaleCapacity') {
          const allDrops = Number(Math.ceil(v.target/v.capacity)).toFixed() || values.allDrops;
          const data = batchStore.calculateDrops({
            quantityToBeBatched: values.quantityToBeBatched || 1,
            mixQuantityInTruck: values.mixQuantityInTruck || 0,
            allDrops,
          });

          payload = {
            ...values,
            allDrops: data.allDrops,
            dropSize: data.dropSize,
          };

          logger.warn(`${v.error} Adjusting drops ${allDrops}`);
          info.push(`The load was split into ${allDrops} drops because ${v.error}`);
        }
        if (v.name === 'maximumAmountInLoad' || v.name === 'maximumDropLimit') {
          const allDrops = Number(Math.ceil(v.target/v.limit)).toFixed() || values.allDrops;
          const data = batchStore.calculateDrops({
            quantityToBeBatched: values.quantityToBeBatched || 1,
            mixQuantityInTruck: values.mixQuantityInTruck || 0,
            allDrops,
          });

          if (payload.allDrops < allDrops) {
            payload = {
              ...values,
              allDrops: data.allDrops,
              dropSize: data.dropSize,
            };

            logger.warn(`${v.error} Adjusting drops ${allDrops}`);
            info.push(`The load was split into ${allDrops} drops because ${v.error}`);
          }
        }

      });
    }

    payload = { ...values, ...payload };

    if (setValues) {
      setValues(payload);
    }
    if (info.length) {
      this.setState({
        info: [ ...info ],
      });
    }

    return payload;
  }

  onAdjustedMixDesignChange = (adjustedMixDesignId, values, setValues) => {
    const { batchStore } = this.props;
    let payload = {
      ...values,
      ...adjustMixDesign(adjustedMixDesignId, values, values.mixId, batchStore.rootStore),
    };

    if (payload.secondWater) {
      payload = this.addSecondWater(payload);
    }

    setValues(calculateMixDesign(payload, batchStore.rootStore));
  };

  onMixerChange = (mixerId, values, setValues) => {
    let payload = {
      ...values,
      mixerId,
      dischargeSequenceId: this.getDischargeSequenceId({ ...values, mixerId }),
    };

    setValues(payload);
  };

  getMaxDropSize(mixId) {
    const { mixStore, batchStore } = this.props;
    const mix = mixId ? mixStore.getOnlineMixById(mixId) : null;

    return mix && mix.maxDropSize && mix.maxDropSize < batchStore.maximumDropSize
      ? mix.maxDropSize
      : batchStore.maximumDropSize;
  }

  onSequenceChange(dischargeSequenceId, values, setValues) {
    const payload = {
      ...values,
      dischargeSequenceId: dischargeSequenceId ? Number(dischargeSequenceId) : '',
    };

    setValues(payload);
  }

  onQuantityChange = (quantityToBeBatched, mixQuantityInTruck = 0, values, setValues) => {
    const { batchStore } = this.props;
    const data = batchStore.calculateDrops({
      quantityToBeBatched: quantityToBeBatched || 0,
      mixQuantityInTruck: mixQuantityInTruck || 0,
    }, this.getMaxDropSize(values.mixId));
    const dischargeSequenceId = this.getDischargeSequenceId({ ...values, mixQuantityInTruck, dropSize: data.dropSize });

    const payload = {
      ...values,
      quantityToBeBatched: quantityToBeBatched || 0,
      mixQuantityInTruck: mixQuantityInTruck || 0,
      batchSize: data.batchSize,
      allDrops: data.allDrops,
      dropSize: data.dropSize,
      dischargeSequenceId,
    };

    this.adjustDrops(calculateMixDesign(payload, batchStore.rootStore), setValues);
  };

  onAllDropsChange = (allDrops, values, setValues) => {
    const { batchStore } = this.props;
    const data = batchStore.calculateDrops({
      quantityToBeBatched: values.quantityToBeBatched || 1,
      mixQuantityInTruck: values.mixQuantityInTruck || 0,
      allDrops,
    });

    const dischargeSequenceId = this.getDischargeSequenceId({ ...values, dropSize: data.dropSize });

    const payload = {
      ...values,
      allDrops: data.allDrops,
      dropSize: data.dropSize,
      dischargeSequenceId,
    };

    if (this.state.errors.length) {
      this.verify(payload);
    }

    this.onWaterChange(payload, setValues);
  };

  onIceChange = (ice, values, setValues, iceUnit) => {
    iceUnit = iceUnit || values.iceUnit;
    let payload = {...values, ice, iceUnit };

    this.onWaterChange(payload, setValues);
  };

  onWaterInTruckChange = (waterInTruck, values, setValues) => {
    let payload = { ...values, waterInTruck };

    this.onWaterChange(payload, setValues);
  };

  onSlumpChange = (slump, values, setValues) => {
    let payload = { ...values, slump };

    this.onWaterChange(payload, setValues);
  };

  onWaterChange = (payload, setValues) => {
    const { batchStore } = this.props;
    setValues(calculateMixDesign(payload, batchStore.rootStore));
  };

  onMixInTruckChange(payload, setValues) {
    const { batchStore } = this.props;
    setValues(calculateMixDesign(payload, batchStore.rootStore));
  }

  get secondWater() {
    return this.props.materialStore.secondWater;
  };

  get secondWaterExist() {
    return !!this.secondWater;
  };

  addSecondWater(values) {
    const { materialStore } = this.props;
    const secondWater = this.secondWater;

    if (!values.mixDesign.length || !secondWater) {
      return values;
    }

    let water = values.mixDesign.find(v => materialStore.isWater(v.constituentItemId));
    if (water) {
      water = materialStore.getMaterialByItemId(water.constituentItemId);
    } else {
      water = materialStore.water;
    }

    if (!water) {
      return values;
    }

    const secondWaterIndex = values.mixDesign.findIndex(v => v.constituentItemId === secondWater.itemId);
    const waterIndex = values.mixDesign.findIndex(v => v.constituentItemId === water.itemId);

    if (waterIndex === -1 && secondWaterIndex > -1) {
      const md = water ? materialStore.materialDetails[water.materialDetailId] || {} : {};
      let payload = {...values};

      payload.mixDesign.push({
        id: `water-${water.itemId}`, // must be the same, otherwise formik will reset form to initial values
        constituentItemId: water.itemId,
        constituentItemCode: water.code,
        constituentItemShortDescription: md.itemShortDescription || md.itemDescription || water.code,
        constituentItemDescription: md.itemDescription || water.code,
        unitId: md.itemBatchUnitId,
        unitSymbol: md.itemBatchUnitSymbol,
        quantity: 0,
        quantityNet: 0,
        trim: 0,
        added: true,
      });

      return payload;
    }

    if (secondWaterIndex > -1) {
      return values;
    }

    const md = secondWater ? materialStore.materialDetails[secondWater.materialDetailId] || {} : {};
    let payload = {...values};

    payload.mixDesign.push({
      id: `second-water-${secondWater.itemId}`, // must be the same, otherwise formik will reset form to initial values
      constituentItemId: secondWater.itemId,
      constituentItemCode: secondWater.code,
      constituentItemShortDescription: md.itemShortDescription || md.itemDescription || secondWater.code,
      constituentItemDescription: md.itemDescription || secondWater.code,
      unitId: md.itemBatchUnitId,
      unitSymbol: md.itemBatchUnitSymbol,
      quantity: 0,
      quantityNet: 0,
      trim: 0,
      added: true,
    });

    return payload;
  };

  onSecondWaterChange = (secondWater, values, setValues) => {
    const { batchStore } = this.props;
    let payload = {...values, secondWater };

    if (secondWater > 0) {
      payload = this.addSecondWater(payload);
    }

    this.adjustDrops(calculateMixDesign(payload, batchStore.rootStore), setValues);
  };

  saveSecondWater(secondWater) {
    this.props.newAuthStore.storage.setItem('secondWater', secondWater);
  }

  getSecondWater() {
    return this.props.newAuthStore.storage.getItem('secondWater') || 0;
  }

  onWaterAdjustChange(waterAdjust, values, setValues) {
    const { batchStore } = this.props;
    let payload = {...values, waterAdjust };

    setValues(calculateMixDesign(payload, batchStore.rootStore));
  }

  saveWaterAdjust(waterAdjust = 0, values = {}) {
    const { newAuthStore, settingsStore, mixStore } = this.props;
    const { storage } = newAuthStore;
    const { configuration } = settingsStore;
    let w = storage.getItem('waterAdjusts')  || [];

    if (!configuration.saveWaterAdjustBy) {
      return;
    } else if (configuration.saveWaterAdjustBy === WaterAdjustBy.Order) {
      w = w.filter(v => mixStore.isDateToday(v.date) && v.orderCode !== values.dispatchOrderId && v.value);
    } else if (configuration.saveWaterAdjustBy === WaterAdjustBy.MixDesign) {
      w = w.filter(v => v.mixId !== values.mixId && v.value);
    } else if (configuration.saveWaterAdjustBy === WaterAdjustBy.Day) {
      w = [];
    }

    w.push({
      saveBy: configuration.saveWaterAdjustBy,
      value: waterAdjust,
      orderCode: values.dispatchOrderId,
      mixId: values.mixId,
      date: mixStore.today,
    });

    storage.setItem('waterAdjusts', w);
  }

  getWaterAdjust(dispatchOrderId = '', mixId = '') {
    const { newAuthStore, settingsStore, mixStore } = this.props;
    const { configuration } = settingsStore;
    const w = newAuthStore.storage.getItem('waterAdjusts') || [];
    let wa;

    if (configuration.saveWaterAdjustBy === WaterAdjustBy.Order && dispatchOrderId) {
      wa = w.find(v => v.saveBy === configuration.saveWaterAdjustBy && v.orderCode === dispatchOrderId);
    } else if (configuration.saveWaterAdjustBy === WaterAdjustBy.MixDesign && mixId) {
      wa = w.find(v => v.saveBy === configuration.saveWaterAdjustBy && v.mixId === mixId);
    } else if (configuration.saveWaterAdjustBy === WaterAdjustBy.Day) {
      wa = w.find(v => v.saveBy === configuration.saveWaterAdjustBy && mixStore.isDateToday(v.date));
    }

    return wa ? wa.value : 0;
  }

  onTruckChange = (truckId, values, setValues) => {
    const { scheduleStore } = this.props;
    const truck = scheduleStore.trucks.find(v => v.id === Number(truckId)) || {};
    const driver = truck.driverId ? scheduleStore.drivers.find(v => v.id === truck.driverId) || {} : {};
    const dischargeSequenceId = this.getDischargeSequenceId({ ...values, truckId });

    const payload = {
      ...values,
      truckId,
      truckCode: truck.code,
      driverId: driver.id,
      driverName: driver.name,
      driverCode: driver.code,
      dischargeSequenceId,
    };

    setValues(payload);
  };

  onDriverChange = (driverId, values, setValues) => {
    const { scheduleStore } = this.props;
    const driver = scheduleStore.drivers.find(v => v.id === Number(driverId)) || {};
    const payload = {
      ...values,
      driverId,
      driverName: driver.name,
      driverCode: driver.code,
    };

    setValues(payload);
  };

  onProjectChange = (projectId, values, setValues) => {
    const { scheduleStore } = this.props;
    const project = scheduleStore.projects.find(v => v.id === Number(projectId)) || {};
    const payload = {
      ...values,
      projectId,
      projectName: project.name,
      projectCode: project.code,
    };

    setValues(payload);
  };

  onCustomerChange = (customerId, values, setValues) => {
    const { scheduleStore } = this.props;
    const customer = scheduleStore.customers.find(v => v.id === Number(customerId)) || {};
    const payload = {
      ...values,
      customerId,
      customerName: customer.name,
      customerCode: customer.code,
    };

    setValues(payload);
  };

  getDischargeSequenceId(values) {
    const { dischargeStore, settingsStore, devicesStore } = this.props;
    let sequence;

    if (values.mixId) {
      sequence = dischargeStore.mixCharge.find(v => v.mixItemId === Number(values.mixId));
    }

    if (!sequence && values.mixerId) {
      const mixer = devicesStore.mixers.find(v => v.id === Number(values.mixerId));
      if (mixer && mixer.dischargeSequenceId) {
        return mixer.dischargeSequenceId;
      }
    }

    if (!sequence && values.truckId) {
      sequence = dischargeStore.truckCharge.find(v => v.truckId === Number(values.truckId));
    }

    if (!sequence && values.dropSize) {
      sequence = dischargeStore.loadSizeCharge.find(v => Number(values.dropSize) >= v.loadSize);
    }

    return sequence ? sequence.dischargeSequenceId : settingsStore.configuration.defaultDischargeSequenceId || values.dischargeSequenceId;
  }

  onDropdownSearch = (options, value) => {
    const searchValue = String(value).toLowerCase();

    return options
      .filter(v => v.text.toLowerCase().includes(searchValue))
      .sort((a, b) => {
        const aText = a.text.toLowerCase();
        const bText = b.text.toLowerCase();

        // Exact match first
        if (aText === searchValue && bText !== searchValue) return -1;
        if (bText === searchValue && aText !== searchValue) return 1;

        // Then values that start with the search term
        const aStartsWith = a.text.startsWith(value);
        const bStartsWith = b.text.startsWith(value);

        if (aStartsWith !== bStartsWith) {
          return aStartsWith ? -1 : 1;
        }

        // Finally, sort by position of the match
        return aText.indexOf(searchValue) - bText.indexOf(searchValue);
      });
  };

  onMixChange = (mixId, values, setValues, dischargeSequenceId) => {
    const { batchStore, mixStore, materialStore, devicesStore, settingsStore, scheduleStore } = this.props;
    batchStore.setOrderId(null);
    let mixDesign = checkMixDesignQuantityAndTrim(Number(mixId), values, this.props.mixStore, this.props.materialStore, this.props.batchStore, this.props.devicesStore).mixDesign;
    mixDesign = checkMixDesignOrder(mixDesign, materialStore);
    const water = mixDesign.find(v => materialStore.isWater(v.constituentItemId));
    const waterUnit = water ? materialStore.getMaterialUnitSymbol(water.constituentItemId) : '';
    const mix = mixStore.getOnlineMixById(Number(mixId)) || mixStore.getMixById(Number(mixId)) || {};
    const mixerId = devicesStore.getNextMixer();
    dischargeSequenceId = dischargeSequenceId || this.getDischargeSequenceId({ ...values, mixId, mixerId });
    const data = batchStore.calculateDrops({
      quantityToBeBatched: values.quantityToBeBatched || 0,
      mixQuantityInTruck: values.mixQuantityInTruck || 0,
    }, this.getMaxDropSize(Number(mixId)));

    this.setInitialState();

    let payload = {
      ...values,
      mixId,
      mix,
      mixDesign,
      batchSize: data.batchSize,
      allDrops: data.allDrops,
      dropSize: data.dropSize,
      unitId: mix.unitId,
      slump: mix.slump,
      originalSlump: mix.slump,
      slumpTableId: mix.slumpTableId || settingsStore.configuration?.slumpTableId,
      mixDescription: mix.itemDescription,
      ice: 0,
      secondWater: this.getSecondWater(),
      waterAdjust: this.getWaterAdjust(values.dispatchOrderId, mixId),
      waterInTruck: 0,
      waterUnit,
      dischargeSequenceId,
      mixerId,
      useBay : settingsStore.configuration ? settingsStore.configuration.useBay : false,
      customerId: scheduleStore.customers.length === 1 ? scheduleStore.customers[0].id : '',
    };

    payload = {
      ...payload,
      originalPayload: { ...payload, mixDesign: [ ...mixDesign ] },
    };

    if (this.hasMixDesignDuplicates(mixDesign)) {
      batchStore.showWarning('Mix Design contains duplicate constituent!');
    }

    if (!materialStore.hasBinOnlyOneMaterialAssigned()) {
      batchStore.showWarning('One of the bins is assigned and active on two different materials!');
    }

    this.onSecondWaterChange(payload.secondWater, payload, setValues);

  };

  onMoistureChange = (e, index, values, setValues) => {
    const moisture = toNumber(e.target.value || 0, 2);
    let payload = {...values };
    payload.mixDesign[index] = {
      ...values.mixDesign[index],
      moisture,
    };

    this.onWaterChange(payload, setValues);
  };

  onMixDesignTrimChange = (e, index, values, setValues) => {
    const { batchStore } = this.props;
    const proposedTrim = toNumber(e.target.value, 2) || 0;

    // Validate and limit negative trim to prevent quantityNet from going negative
    const trim = validateNegativeTrim(proposedTrim, index, values, batchStore.rootStore);

    let payload = {...values };
    payload.mixDesign[index] = {
      ...values.mixDesign[index],
      trim,
      manualTrim: true,
    };

    this.adjustDrops(calculateMixDesign(payload, batchStore.rootStore), setValues);
  };

  onMixDesignQuantityChange = (e, index, values, setValues) => {
    const { batchStore } = this.props;
    const quantity = toNumber(e.target.value, 2) || 0;
    const constituent = values.mixDesign[index];

    let payload = {...values };
    payload.mixDesign[index] = {
      ...constituent,
      initialQuantity: constituent.initialQuantity || constituent.quantity,
      quantity,
    };

    this.adjustDrops(calculateMixDesign(payload, batchStore.rootStore), setValues);
  };

  onByCementChange = (e, index, values, setValues) => {
    const { batchStore } = this.props;
    const byCement = e.target.checked;

    let payload = {...values };
    payload.mixDesign[index] = {
      ...values.mixDesign[index],
      byCement: byCement,
    };

    setValues(calculateMixDesign(payload, batchStore.rootStore));
  };

  onMixDesignChange = (mixDesignId, index, values, setValues) => {
    const { materialStore, devicesStore, batchStore } = this.props;
    const material = materialStore.materials.find(v => v.itemId === Number(mixDesignId)) || {};
    const md = materialStore.materialDetails[material.materialDetailId] || {};
    let payload = {...values };

    payload.mixDesign[index] = {
      ...values.mixDesign[index],
      constituentItemId: mixDesignId ? Number(mixDesignId) : '',
      constituentItemCode: material.code,
      constituentItemShortDescription: md.itemShortDescription || md.itemDescription || material.code,
      constituentItemDescription: md.itemDescription || material.code,
      batchCode: material.batchCode,
      batchShortDescription: material.batchShortDescription,
      unitId: md.itemBatchUnitId,
      unitSymbol: md.itemBatchUnitSymbol,
      trim: 0,
      moisture: getMoisture(material.itemId, materialStore),
      byCement: md.byCement,
    };

    if (devicesStore.getActiveBinsForMultiScale(payload.mixDesign[index])) {
      payload.mixDesign[index].binPercent = getBinPercent(payload.mixDesign[index], materialStore);
    }

    setValues(calculateMixDesign(payload, batchStore.rootStore));
  };

  onSaveClick = (submitForm) => {
    const { scheduleStore } = this.props;
    scheduleStore.isPreSaveOrder = true;
    submitForm();
  };

  onTestClick(submitForm) {
    this.onBatchClick(submitForm, false, true);
  }

  onForceBatchClick(submitForm) {
    this.confirmForceBatchWithInitials(() => {
      this.onBatchClick(submitForm, true);
    });
  }

  onForceDemoClick(submitForm) {
    this.confirmForceBatchWithInitials(() => {
      this.onDemoClick(submitForm, true);
    });
  }

  confirmForceBatchWithInitials(onConfirmed = () => {}) {
    const { t } = this.props;

    const onConfirm = (e) => {
      this.setState({
        forceBatchInitials: {
          eventType: BatchEventType.FORCE_BATCH,
          author: e.target.value,
          time: new Date().toISOString(),
        },
      });
      logger.log('Force Batch', e.target.value);
    };

    showConfirmModal({
      title: t('Confirm the Force Batch'),
      message: <ConfirmWithInitials
        onConfirm={onConfirm}
        text={'Please enter your initials to confirm the Force Batch'}
      />,
      okLabel: t('Confirm'),
      cancelLabel: t('Cancel'),
      enableEscape: false,
    })
      .then(() => {
        setTimeout(() => {
          onConfirmed();
        }, 1000);
      })
      .catch((e) => {
        logger.warn(e);
      });

  }

  onDemoClick(submitForm, batchOffline = false) {
    const { batchStore, diagnoseStore } = this.props;
    if (!batchStore.isDemo && batchStore.running.length) {
      batchStore.showWarning('Can\'t run DEMO while batching!');
      return;
    }

    if (diagnoseStore.isTesting) {
      batchStore.showWarning('Can\'t run DEMO while running Diagnose Auto Test!');
      return;
    }

    if (batchStore.jogTestRunning) {
      batchStore.showWarning('Can\'t run DEMO while running Auto Jog Test!');
      return;
    }

    batchStore.isDemo = true;
    batchStore.isTestMode = false;
    batchStore.batchOffline = batchOffline;

    submitForm();
  };

  verify = (values, setState = true) => {
    const { ticketStore, settingsStore } = this.props;
    const { configuration } = settingsStore;

    if (!values.mix || !values.mixDesign) {
      return;
    }

    let payload = {
      ...values,
      products: [
        {
          ...values.mix,
          isMix: true,
          unitPrice: values.mix.unitPrice || values.mix.price,
          quantity: values.quantityToBeBatched,
        },
      ].concat(values.extra),
      mixSlump: values.slump,
      orderMixQuantity: values.quantityToBeBatched,
      loadStartTime: moment().tz(ticketStore.timezone).format(),
      code: values.code,
      taxRate: values.order ? values.order.taxRate : 0,
      mixDesign: this.changeMixDesignOrder(values.mixDesign),
      adjustedWater: configuration.waterAdjustByLoad
        ? (Number(values.waterAdjust) || 0)
        : (Number(values.waterAdjust) || 0) * Number(values.batchSize),
      isMeteredWaterInTheMix: isMeteredWaterInTheMix(values, this.props.materialStore),
      isWeighedWaterInTheMix: isWeighedWaterInTheMix(values, this.props.materialStore),
    };

    this.setFinalQuantity(payload);

    const verification = canBatch(payload, settingsStore.rootStore);

    if (setState) {
      this.setState({
        errorsToOverride: verification.errorsToOverride,
        errors: verification.errors,
        error: '',
        warnings: verification.warnings,
        mixDifferWarnings: verification.mixDifferWarnings,
        enableOfflineBatch: false,
      });
    }

    return { ...payload, verification };

  };

  constituentShouldBeDisabledForIndicatorError = (constituent) => {
    const { materialStore, devicesStore } = this.props;
    const { errorsToOverride } = this.state;
    const errors = errorsToOverride.filter(v => v.name === 'indicatorValueError');
    let disabled = false;

    errors.forEach(error => {
      const material = constituent.batchCode
        ? materialStore.getMaterialByBatchCode(constituent.batchCode)
        : materialStore.getMaterialByItemId(constituent.constituentItemId);

      const bins = devicesStore.getActiveBinsForMultiScale(constituent)
        || [devicesStore.getPrimaryBin(material)];

      if (material?.scaleId === error.scaleId || bins.some(bin => bin?.scaleId === error.scaleId)) {
        disabled = true;
      }
    });

    return disabled;
  };

  onOverrideErrors = (e, values, setValues) => {
    const { batchStore, t } = this.props;
    const { errorsToOverride } = this.state;
    const checked = e.target.checked;

    const onConfirm = (e) => {
      this.setState({
        forceIndicatorErrorInitials: {
          eventType: BatchEventType.INDICATOR_VALUE_ERROR,
          author: e.target.value,
          time: new Date().toISOString(),
        },
      });
      logger.log('Force Batch', e.target.value);
    };

    const errors = errorsToOverride.filter(v => v.name === 'indicatorValueError');

    if (errors.length) {
      if (!checked) {
        setValues({ ...values, overrideErrors: checked });
      } else {
        showConfirmModal({
          title: t('Confirm the Scale Indicator Error'),
          message: <ConfirmWithInitials onConfirm={onConfirm} />,
          okLabel: t('OK'),
          cancelLabel: t('No'),
          enableEscape: false,
        })
          .then(() => {
            if (this.state.forceIndicatorErrorInitials) {
              values.mixDesign.forEach(constituent => {
                if (this.constituentShouldBeDisabledForIndicatorError(constituent)) {
                  constituent.quantity = 0;
                  constituent.trim = 0;
                  logger.log('clear quantity with scale indicator error', constituent.constituentItemCode);
                }
              });
              this.adjustDrops(calculateMixDesign({ ...values, overrideErrors: checked }, batchStore.rootStore), setValues);
            }
          })
          .catch((e) => {
            logger.warn(e);
          });
      }
    } else {
      setValues({ ...values, overrideErrors: checked });
    }
  };

  onBatchClick = (submitForm, batchOffline = false, isTest = false) => {
    const { batchStore, diagnoseStore } = this.props;

    if (batchStore.isDemo && batchStore.running.length) {
      batchStore.showWarning('Can not BATCH while running DEMO!');
      return;
    }

    if (batchStore.isTestMode && batchStore.running.length) {
      batchStore.showWarning('Only one load allowed during the Test!');
      return;
    }

    if (diagnoseStore.isTesting) {
      batchStore.showWarning('Can not BATCH while running Diagnose Auto Test!');
      return;
    }

    if (batchStore.jogTestRunning) {
      batchStore.showWarning('Can not BATCH while running Auto Jog Test!');
      return;
    }

    batchStore.isTestMode = isTest;
    batchStore.isDemo = false;
    batchStore.batchOffline = batchOffline;

    submitForm();
  };

  isMaximumAmountInMixExceeded(constituent, values) {
    const { materialStore } = this.props;
    const material = constituent.batchCode ? materialStore.getMaterialByBatchCode(constituent.batchCode) : materialStore.getMaterialByItemId(constituent.constituentItemId);
    const materialDetails = material ? materialStore.materialDetails[material.materialDetailId] : null;

    if (!materialDetails) {
      return false;
    }

    const qty = this.getFinalQuantity(constituent, values);

    return materialDetails.maximumAmountInMix > 0 && qty > materialDetails.maximumAmountInMix;

  }

  isMaximumAmountInLoadExceeded(constituent, values, setValues) {
    const { materialStore } = this.props;
    const material = constituent.batchCode ? materialStore.getMaterialByBatchCode(constituent.batchCode) : materialStore.getMaterialByItemId(constituent.constituentItemId);
    const materialDetails = material ? materialStore.materialDetails[material.materialDetailId] : null;

    if (!materialDetails) {
      return false;
    }

    const qty = this.getFinalQuantity(constituent, values) * values.dropSize;

    return materialDetails.maximumAmountInLoad > 0 && qty > materialDetails.maximumAmountInLoad;

  }

  splitTheLoad(values, setValues) {
    this.adjustDrops(calculateMixDesign(values, this.props.batchStore.rootStore), setValues);
  }

  getFinalQuantity(constituent, values) {
    const { batchStore, materialStore, settingsStore } = this.props;
    const { configuration } = settingsStore;
    const material = constituent.batchCode ? materialStore.getMaterialByBatchCode(constituent.batchCode) : materialStore.getMaterialByItemId(constituent.constituentItemId);
    const materialDetails = material ? materialStore.materialDetails[material.materialDetailId] : null;

    if (!material || !materialDetails) {
      if (constituent.constituentItemCode) {
        batchStore.showWarning({ key: 'Missing material', props: { materialCode: materialStore.getConstituentName(constituent) }});
      }
      return 0;
    }

    // A skip material is only recorded on the ticket, so it needs no bin or pump.
    const isSkipWithoutDevice = materialStore.isSkipMaterialWithoutDevice(constituent);

    if (!isSkipWithoutDevice && !materialStore.isDeviceSetToConstituent(constituent)) {
      return 0;
    }

    if (
      !isSkipWithoutDevice
      && !configuration?.enableToggleMaterialVisibility
      && !materialStore.isConstituentBinActive(constituent)
    ) {
      return 0;
    }

    if (batchStore.replayMode && constituent?.quantityFinalOriginal) {
      return constituent.quantityFinalOriginal;
    }

    let qty = 0;

    if (material.assignment === MaterialType.Weighed) {
      qty = batchStore.getTargetForWeighed(constituent, values, values.moisture, false) || 0;
    } else if (material.assignment === MaterialType.Metered) {
      qty = batchStore.getTargetForMetered(material, materialDetails, constituent, values, false) || 0;
    }

    return qty;
  };

  // Returns the final batch quantity with rounding applied for metered materials
  // For example, if the material is metered and the roundOffMetered setting is true,
  // it will round down the final quantity to the nearest meter rate.
  getFinalBatchQuantityWithRounding(constituent, values) {
    const { materialStore, settingsStore, batchStore } = this.props;
    const material = constituent.batchCode ? materialStore.getMaterialByBatchCode(constituent.batchCode) : materialStore.getMaterialByItemId(constituent.constituentItemId);
    const materialDetails = material ? materialStore.materialDetails[material.materialDetailId] : null;

    if (!material || !materialDetails) {
      if (constituent.constituentItemCode) {
        batchStore.showWarning({ key: 'Missing material', props: { materialCode: materialStore.getConstituentName(constituent) }});
      }
      return 0;
    }

    const roundOffMetered = settingsStore.configuration.roundOffMetered;
    const finalQuantityWithoutRounding = this.getFinalQuantity(constituent, values) * values.batchSize;
    const meterRate = materialDetails.meterRate || 1;

    if (
      !roundOffMetered ||
      material.assignment !== MaterialType.Metered
    ) {
      return finalQuantityWithoutRounding;
    }

    return Math.floor((finalQuantityWithoutRounding / meterRate) + 1/2) * meterRate;
  }

  getFinalDropQuantityWithRounding(constituent, values) {
    if (values.allDrops > 1) {
      return this.getFinalQuantity(constituent, values) * values.dropSize;
    }
    // If there is only one drop, we can use the final quantity directly
    return this.getFinalBatchQuantityWithRounding(constituent, values);
  }

  itemWithoutRounding(item) {
    const { materialStore } = this.props;
    const material = item.batchCode
      ? materialStore.getMaterialByBatchCode(item.batchCode)
      : materialStore.getMaterialByItemId(item.constituentItemId);

    return material?.assignment === MaterialType.Metered
      && (
        materialStore.isAdmixture(item.constituentItemId) ||
        materialStore.isWater(item.constituentItemId) ||
        materialStore.isSecondWater(item.constituentItemId)
      );
  }

  setFinalQuantity(values) {
    values.mixDesign.forEach(item => {
      item.quantityFinal = this.getFinalQuantity(item, values);
      item.quantityBatchSize = toNumber(this.getFinalBatchQuantityWithRounding(item, values), this.itemWithoutRounding(item) ? 2 : 0);
    });
  };

  calculateYield(values) {
    const { materialStore } = this.props;
    return materialStore.calculateYield(values.mixDesign);
  }

  checkMixerSelection(values) {
    const { devicesStore, batchStore } = this.props;
    const mixer = devicesStore.mixers.find(v => v.diverterPNo) || devicesStore.mixers[0];
    const isMixerOff = mixer && mixer.diverterPNo && batchStore.isOutputOn(mixer.diverterPNo);

    if (mixer && ((isMixerOff && values.mixerId) || (!isMixerOff && !values.mixerId))) {
      return showConfirmModal({
        title: 'Mixer selection',
        message: 'Mixer selection is different than mixer state. Please change the selection or continue without a change',
        okLabel: 'Continue',
        cancelLabel: 'Change',
        enableEscape: false,
      });
    }

    return Promise.resolve();

  }

  closeSidebar() {
    const { sidebarStore, settingsStore, batchStore } = this.props;
    const { configuration } = settingsStore;

    if (batchStore.isTestMode) {
      sidebarStore.onToggle('testMode');
    } else if (configuration.keepSchedulerOpen) {
      sidebarStore.onToggle('schedules');
    } else {
      sidebarStore.onToggle(null);
    }
  }

  batchInOfflineMode(payload, actions) {
    const { batchStore, ticketStore, printStore, scheduleStore, settingsStore } = this.props;
    const { configuration } = settingsStore;
    const { numberOfTicketCopies, disableLoadPrint } = configuration;
    const ticket = ticketStore.calculateTicketData({ ...payload });
    const dataToBatch = {
      ...payload,
      ...ticket,
      batchStartedOffline: true,
      events: payload.events || [],
    };

    logger.log('batchInOfflineMode', dataToBatch);

    if (payload.printWithoutWeights && !disableLoadPrint) {
      printStore.printTemplate({ ...ticket, isDuplicate: false }, numberOfTicketCopies);
    }

    batchStore.initData(dataToBatch);

    this.setState({ forceBatchInitials: null, forceIndicatorErrorInitials: null });

    this.closeSidebar();

    setTimeout(() => {
      actions.setSubmitting(false);
      batchStore.setOrderId(null);
      batchStore.batchOffline = false;
      scheduleStore.hideOfflineTicketsInScheduler();
      actions.resetForm({
        values: this.getInitialValues(),
      });
    }, 500);
  }

  checkResponseTime() {
    this.requestTimer = setTimeout(() => {
      this.setState({
        error: 'API response takes longer than expected. Please wait or Force Batch',
        enableOfflineBatch: true,
      });
    }, 30*1000);
  }

  onSubmit = (values, actions) => {
    const { batchStore, ticketStore, devicesStore, printStore, connectionStore, settingsStore, scheduleStore, mixStore, t } = this.props;
    const { configuration } = settingsStore;

    actions.setSubmitting(true);

    this.setInitialState();

    const initialData = this.getInitialValues();

    scheduleStore.cancelEdit();
    scheduleStore.preSaveOrder(values);

    if (scheduleStore.isPreSaveOrder) {

      scheduleStore.isPreSaveOrder = false;

      setTimeout(() => {
        actions.setSubmitting(false);
        batchStore.setOrderId(null);
        actions.resetForm({
          values: initialData,
        });
      }, 100);

      return;
    }

    if (batchStore.isAbort) {
      batchStore.showWarning('Please wait when ABORT finish!');
      actions.setSubmitting(false);
      return;
    }

    this.checkMixerSelection(values)
      .then(() => {
        let payload = this.verify(values);
        const configData = {
          isDemo: batchStore.isDemo,
          isTestMode: batchStore.isTestMode,
          runMode: configuration.runMode,
        };

        if (!payload || !payload?.verification.isValid) {
          logger.log('BatchForm: verify failed', payload?.verification);
          actions.setSubmitting(false);
          return;
        } else {
          payload = {
            ...payload,
            ...configData,
            events: [],
          };

          if (this.state.forceBatchInitials) {
            payload.events.push(this.state.forceBatchInitials);
          }

          if (this.state.forceIndicatorErrorInitials) {
            payload.events.push(this.state.forceIndicatorErrorInitials);
          }
        }
        logger.log('payload', payload, this.state);

        if (!batchStore.isDemo && !batchStore.pacConnected && !batchStore.canSendRemoteInstruction()) {
          this.setState({
            error: t('Please check Plant connection before batch!'),
          });
          actions.setSubmitting(false);
          return;
        }

        if (!this.state.autoAdjusted && !payload.adjustedMixes?.length) {
          mixStore.saveMixDesignsWithTrim(payload.mixDesign, payload.dispatchOrderId, payload.mixId);
          this.saveSecondWater(payload.secondWater);
          this.saveWaterAdjust(payload.waterAdjust, payload);
        }

        savePreviousValues(values, settingsStore);

        devicesStore.setMixerId(payload.mixerId);

        this.setInitialState();
        this.setState({
          overrideErrors: false,
          forceBatchInitials: null,
          forceIndicatorErrorInitials: null,
        });

        if (batchStore.isDemo && !batchStore.canDemoBatch) {
          this.closeSidebar();

          setTimeout(() => {
            actions.setSubmitting(false);
            batchStore.setOrderId(null);
            actions.resetForm({
              values: initialData,
            });
            batchStore.initData({
              ...payload,
              batchStartedRemotely: batchStore.canSendRemoteInstruction(),
            });
          }, 500);
          return;
        }

        if (!connectionStore.isOnline || batchStore.batchOffline) {
          this.batchInOfflineMode({ ...payload }, actions);
          return;
        }

        values = {
          ...values,
          ...configData,
          adjustedWater: configuration.waterAdjustByLoad
            ? (Number(values.waterAdjust) || 0)
            : (Number(values.waterAdjust) || 0) * Number(values.batchSize),
        };

        this.checkResponseTime();

        if (values.origin === OrderOrigin.External) {
          values.truckId = null;
          values.driverId = null;
          values.customerId = null;
        }

        if (values.customerId < 0) {
          values.customerId = null;
        }

        let promise;
        if (payload.id) {
          promise = values.origin === OrderOrigin.External || values.origin === OrderOrigin.Local
            ? ticketStore.createLocalOrderOnBatchStart(values)
            : ticketStore.createTicketOnBatchStart(values);
        } else {
          promise = ticketStore.createTicketAndLocalOrder(values);
        }

        promise
          .then((data) => {
            this.closeSidebar();

            if (data.batchOrderId) {
              const offlineOrdersIds = scheduleStore.offlineOrders.map(v => v.id);
              const currentLoadsIds = batchStore.running.map(v => v.id);
              const offlineTicketsIds = ticketStore.offlineTicketsFromDispatch.map(v => v.id);

              if ([...offlineTicketsIds, ...offlineOrdersIds, ...currentLoadsIds].includes(data.batchOrderId)) {
                scheduleStore.hideOfflineTicketsInScheduler();
                logger.log(`ticket #${data.batchOrderId} has already started (offline?)!`,
                  offlineTicketsIds, offlineOrdersIds, currentLoadsIds);
                return;
              }
            }

            setTimeout(() => {
              actions.setSubmitting(false);
              batchStore.setOrderId(null);
              actions.resetForm({
                values: initialData,
              });
            }, 500);

            const id = data.origin === OrderOrigin.Local && !payload.id && data.ticket ? data.ticket.id : data.id;
            const batchOrderId = data.origin === OrderOrigin.Local && !payload.id ? data.id : data.batchOrderId;
            const dataToBatch = {
              ...payload,
              id,
              dispatchOrderCode: data.dispatchOrderCode,
              dispatchTicketCode: data.dispatchTicketCode,
              batchOrderCode: data.batchOrderCode,
              batchOrderId,
              code: data.code,
              ticket: data.ticket,
              orderedBy: data.ticket ? data.ticket.orderedBy : data.orderedBy,
              batchStartedRemotely: batchStore.canSendRemoteInstruction(),
            };
            const { printWithoutWeightsTemplateId, numberOfTicketCopies, disableLoadPrint } = settingsStore.configuration;

            if (payload.printWithoutWeights && !dataToBatch.batchStartedRemotely && !disableLoadPrint) {
              let ticket = data.ticket ? data.ticket : data;
              ticket = ticketStore.calculateTicketData({
                ...ticket,
                loadStartTime: moment().tz(ticketStore.timezone).format(),
                loadEndTime: !dataToBatch.batchSize ? moment().tz(ticketStore.timezone).format() : null,
              });
              ticketStore.getTemplate(printWithoutWeightsTemplateId)
                .finally(() => {
                  printStore.printTemplate({ ...ticket, isDuplicate: false }, numberOfTicketCopies)
                    .finally(() => {
                      batchStore.initData(dataToBatch);
                    });
                });
            } else {
              batchStore.initData(dataToBatch);
            }

            this.setInitialState();

            this.updateMoistureChange(values);
          })
          .catch((e) => {
            this.setState({
              error: `API error: ${e.message} Please try again or Force Batch`,
              errors: [],
              warnings: [],
              mixDifferWarnings: [],
              enableOfflineBatch: true,
            });
            actions.setValues(values);
            actions.setSubmitting(false);
          })
          .finally(() => {
            clearTimeout(this.requestTimer);
          });

      })
      .catch(() => actions.setSubmitting(false));
  };

  onResetForm = resetForm => {
    const { batchStore } = this.props;
    resetForm({
      values: this.getInitialValues(),
    });
    batchStore.setOrderId(null);

    this.setInitialState();
  };

  updateMoistureChange = (values) => {
    const { materialStore } = this.props;
    const initialMixDesign = values.originalPayload?.mixDesign ? [ ...values.originalPayload.mixDesign ] : [];
    const mixDesign = values?.mixDesign ? [ ...values.mixDesign ] : [];

    mixDesign.forEach(item => {
      const constituent = initialMixDesign.find(v => v.constituentItemId === item.constituentItemId);
      if (constituent && constituent.moisture !== item.moisture) {
        const material = constituent.batchCode
          ? materialStore.getMaterialByBatchCode(constituent.batchCode)
          : materialStore.getMaterialByItemId(constituent.constituentItemId);
        const materialDetails = material ? materialStore.materialDetails[material.materialDetailId] : null;
        if (materialDetails) {
          materialStore.saveMaterialDetail(material, { ...materialDetails, moistureAmount: item.moisture });
        }
        logger.log('update moisture', material?.code, item.moisture);
      }
    });
  };

  changeMixDesignOrder = mixDesign => {
    mixDesign.forEach((item, index) => {
      item.order = index + 1;
    });

    return mixDesign;
  };

  reorderDragList = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  getDragItemStyle = (isDragging, draggableStyle) => ({
    background: isDragging ? '#eee' : '',
    borderBottom: '0 none',
    ...draggableStyle,
  });

  onDragEnd = (result, values) => {
    if (!result.destination) {
      return;
    }

    const orderedData = this.reorderDragList(
      values.mixDesign,
      result.source.index,
      result.destination.index
    );

    values.mixDesign = this.changeMixDesignOrder(orderedData);
  };

  renderAdjustedMixDesign = values => {
    const { settingsStore } = this.props;
    return (
      <Panel>
        <Panel.Body>
          <Row>
            <Col md={12} xs={12}>
              <FieldArray
                name="adjusted"
                render={() => (
                  <div>
                    {values.adjustedMixes.map((mix, index) => (
                      <Row key={index}>
                        <Col md={2} xs={6}>
                          {mix.extraProductItemCode}
                        </Col>
                        <Col md={4} xsHidden={true}>
                          {mix.extraProductItemDescription}
                        </Col>
                        <Col md={6} xs={6}>
                          {mix.constituentItems.map((extra, i) => (
                            <Row key={i}>
                              <Col md={4} xs={6}>
                                {extra.constituentItemCode}
                              </Col>
                              <Col md={6} xs={6}>
                                {extra.quantity}{' '}
                                {getQuantityTypeUnitForAdjustedMix(extra, settingsStore)}
                              </Col>
                            </Row>
                          ))}
                        </Col>
                      </Row>
                    ))}
                  </div>
                )}
              />
            </Col>
          </Row>
        </Panel.Body>
      </Panel>
    );
  };

  renderExtraRow = (values) => {
    return (
      <FieldArray
        name="extra"
        render={() => (
          <div className={'extra-product-list'}>
            <Panel>
              <Panel.Body>
                {values.extra.map((extra, index) => (
                  <Row key={index}>
                    <Col md={2} xs={6}>{extra.itemCode}</Col>
                    <Col md={4} xsHidden={true}>{extra.itemDescription}</Col>
                    <Col md={6} xs={6}><strong>{extra.quantity}</strong></Col>
                  </Row>
                ))}
              </Panel.Body>
            </Panel>
          </div>
        )}
      />
    );
  };

  renderMaterialDivisionRow = (values, item, index, setValues) => {
    const { devicesStore, t } = this.props;
    const { showBins } = this.state;
    const bins = devicesStore.getActiveBinsForMultiScale(values.mixDesign[index]);

    if (!values.mixDesign[index].binPercent) {
      values.mixDesign[index].binPercent = {};
    }

    const getTotalPercent = () => {
      let percent = 0;
      Object.values(values.mixDesign[index].binPercent).forEach(v => {
        percent += Number(v) || 0;
      });

      return percent;
    };

    const getValuePerBin = (index, binId) => {
      const qty = values.mixDesign[index].quantity || 0;
      const percent = values.mixDesign[index].binPercent[binId] || 0;

      return toNumber(qty * percent/100, 1);
    };

    const onPercentChange = (value, binId) => {
      if (Number(value) >= 100) {
        Object.keys(values.mixDesign[index].binPercent).forEach(bId => {
          if (binId !== bId) {
            values.mixDesign[index].binPercent[bId] = 0;
          }
        });
      }

      values.mixDesign[index].binPercent[binId] = Number(value);

      setValues(values);
    };

    return bins && bins.length && showBins
      ? (
        <div className="material-division-wrap">
          {bins.map((bin) => (
            <Row key={bin.id || index}>
              <Col sm={2} xs={4}>
                <ControlLabel>{t('Bin')}</ControlLabel>
                <div className="form-data-value">
                  {bin.description}
                </div>
              </Col>
              <Col sm={2} xs={4}>
                <ControlLabel>{t('Split')}</ControlLabel>
                <FormGroup className={getTotalPercent() > 100 ? 'has-error' : ''}>
                  <InputGroup>
                    <Field name={`mixDesign.${index}.binPercent.${bin.id}`}
                      type="number"
                      min={0}
                      max={100}
                      maxLength={3}
                      step={1}
                      value={values.mixDesign[index].binPercent[bin.id] || ''}
                      onChange={(e) => onPercentChange(e.target.value, bin.id)}
                      className="form-control input-sm narrow-pad"/>
                    <InputGroup.Addon>
                    %
                    </InputGroup.Addon>
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col sm={4}/>
              <Col sm={1} xs={2}>
                <FormGroup>
                  <ControlLabel>{t('Mix')}</ControlLabel>
                  <div className="form-data-value">{toNumber(getValuePerBin(index, bin.id) || 0, 1)}
                  </div>
                </FormGroup>
              </Col>
              <Col sm={1} xs={2}>
                <FormGroup>
                  <ControlLabel>{t('Drop')}</ControlLabel>
                  <div className="form-data-value">{toNumber(getValuePerBin(index, bin.id) * values.dropSize || 0, 0)}
                  </div>
                </FormGroup>
              </Col>
              <Col sm={1} xsHidden={true}>
                <FormGroup>
                  <ControlLabel>{t('Batch')}</ControlLabel>
                  <div className="form-data-value">{toNumber((getValuePerBin(index, bin.id) * values.batchSize) || 0, 0)}
                  </div>
                </FormGroup>
              </Col>
            </Row>
          )
          )}
        </div>
      )

      : '';
  };

  renderMixDesignRow = (values, item, index, setFieldValue, arrayHelpers, errors, touched, setValues) => {
    const { materialStore, settingsStore, devicesStore, ticketStore, newAuthStore, t } = this.props;
    const { showBins } = this.state;
    const { materialsWithDetails } = materialStore;
    const { configuration } = settingsStore;
    const filteredMaterials = materialsWithDetails.filter(v => !values.mixDesign.find(x => x.constituentItemId === v.itemId));
    const bins = devicesStore.getActiveBinsForMultiScale(values.mixDesign[index]);
    const globalWaterUnitSymbol = ticketStore.getWaterUnit();
    const unit = materialStore.getConstituentUnitSymbol(item);
    const isConversionNeeded = isWaterOrSecondWater(item.constituentItemId, this.props.materialStore)
      && unit !== globalWaterUnitSymbol
      && convertUnit(1, unit, globalWaterUnitSymbol) !== 1;

    const toggleBins = () => {
      this.setState({
        showBins: !this.state.showBins,
      });
    };

    return (
      <Row key={item.id || index} className={`mix-design-row ${materialStore.isWater(item.constituentItemId) ? 'is-water' : ''}`}>
        <Col sm={2} xs={4}>
          <FormGroup className={cx({'has-error': errors.mixDesign && errors.mixDesign[index] && errors.mixDesign[index].constituentItemId} )}>
            <ControlLabel data-e2e-id={`lbl-mix-design-constituent-${index}`}>{t('Constituent')}</ControlLabel>
            {!values.mixDesign[index].added && !configuration.localDispatchOrderChange
              ? (
                <div
                  id={`mix-design-constituent-name-${index}`}
                  data-e2e-id={`mix-design-constituent-name-${index}`}
                  className="form-data-value">
                  {materialStore.getConstituentName(item)}
                  {bins && bins.length && (
                    <a className="btn btn-sm btn-default btn-action pull-right"
                      onClick={() => toggleBins()}>
                      <i className={showBins ? 'fa fa-angle-up' : 'fa fa-angle-down'}/>
                    </a>
                  )}
                </div>
              )
              : (
                <React.Fragment>
                  <FormControl
                    id={`mix-design-constituent-${index}`}
                    data-e2e-id={`mix-design-constituent-name-${index}`}
                    name={`mixDesign.${index}.constituentItemId`}
                    componentClass="select"
                    value={item.constituentItemId || ''}
                    onChange={e => this.onMixDesignChange(e.target.value, index, values, setValues)}
                    bsSize="sm"
                  >
                    {filteredMaterials.map(material => (
                      <option key={material.itemId} value={material.itemId}>
                        {materialStore.getMaterialName(material)}
                      </option>
                    ))}
                    <option value={item.constituentItemId || ''}>
                      {materialStore.getConstituentName(item)}
                    </option>
                  </FormControl>
                  {bins && bins.length && (
                    <a className="btn btn-sm btn-default btn-action pull-right"
                      onClick={() => toggleBins()}>
                      <i className={showBins ? 'fa fa-angle-up' : 'fa fa-angle-down'}/>
                    </a>
                  )}
                </React.Fragment>
              )}
            <ErrorMessage component="span" name={`mixDesign.${index}.constituentItemId`} className="help-block" />
            {!materialStore.isConstituentAssigned(item) &&
              <div className="has-error"><span className="help-block">{t('Unassigned')}!</span></div>
            }
            {materialStore.isConstituentAssigned(item) &&
            !materialStore.isDeviceSetToConstituent(item) &&
            !materialStore.isSkipMaterialWithoutDevice(item) &&
              <div className="has-error"><span className="help-block">{t('No device set')}!</span></div>
            }
            {!materialStore.isConstituentBinActive(item) && !materialStore.isSkipMaterialWithoutDevice(item) &&
              <div className="has-error"><span className="help-block">{t('No active bin')}!</span></div>
            }
          </FormGroup>
        </Col>
        <Col sm={1} xs={2}>
          <FormGroup>
            <ControlLabel data-e2e-id={`lbl-mix-design-quantity-${index}`}>{t('Qty')}</ControlLabel>
            {newAuthStore.user.hasAccess(PERMISSION.BATCH_MODIFY_MIX_DESIGN_QUANTITY)
              ? (
                <Field name={`mixDesign.${index}.quantity`}
                  id={`mix-design-quantity-${index}`}
                  data-e2e-id={`mix-design-quantity-${index}`}
                  type="number"
                  min={0}
                  value={item.quantity || ''}
                  disabled={this.constituentShouldBeDisabledForIndicatorError(item)}
                  onChange={e => this.onMixDesignQuantityChange(e, index, values, setValues)}
                  className="form-control input-sm narrow-pad"
                />
              ) : (
                <div className="form-data-value">
                  {item.quantity || 0}
                  <span> {unit}</span>
                  {isConversionNeeded &&
                  <div>{toNumber(convertUnit(item.quantity, unit, globalWaterUnitSymbol), 2)} {globalWaterUnitSymbol}</div>
                  }
                </div>
              )
            }
          </FormGroup>
        </Col>
        <Col sm={2} xsHidden={true}>
          <FormGroup className={cx({'has-error': errors.mixDesign && errors.mixDesign[index] && errors.mixDesign[index].moisture
              && touched.mixDesign && touched.mixDesign[index] && touched.mixDesign[index].moisture} )}>
            <ControlLabel data-e2e-id={`lbl-mix-design-moisture-${index}`}>{t('Moist%')}</ControlLabel>
            {!!this.props.materialStore.isAggregate(item.constituentItemId) &&
              (this.isAutoMoisture(item.constituentItemId) || this.isLiveMoisture(item.constituentItemId)) &&
              (
                <div id={`mix-design-constituent-auto-moisture-${index}`} data-e2e-id={`mix-design-constituent-auto-moisture-${index}`} className="form-data-value">
                  {this.isLiveMoisture(item.constituentItemId) ? t('Live') : t('Auto')}: {item.moisture}
                </div>
              )
            }
            {!!this.props.materialStore.isAggregate(item.constituentItemId) &&
            !this.isAutoMoisture(item.constituentItemId) &&
            !this.isLiveMoisture(item.constituentItemId) &&
              (
                <Field name={`mixDesign.${index}.moisture`}
                  id={`mix-design-constituent-moisture-${index}`}
                  data-e2e-id={`mix-design-constituent-moisture-${index}`}
                  type="number"
                  min={-100}
                  max={100}
                  step={0.01}
                  value={item.moisture || ''}
                  disabled={this.constituentShouldBeDisabledForIndicatorError(item)}
                  onChange={e => this.onMoistureChange(e, index, values, setValues)}
                  className="form-control input-sm narrow-pad"/>
              )
            }
            <ErrorMessage component="span" name={`mixDesign.${index}.moisture`} className="help-block" />
          </FormGroup>
        </Col>
        <Col sm={2} xs={2}>
          <FormGroup className={cx({'has-error': errors.mixDesign && errors.mixDesign[index] && errors.mixDesign[index].trim && touched.mixDesign && touched.mixDesign[index] && touched.mixDesign[index].trim} )}>
            <ControlLabel data-e2e-id={`lbl-mix-design-trim-${index}`}>{t('Trim')}</ControlLabel>
            {materialStore.isConstituentAssigned(item) && (
              <Field name={`mixDesign.${index}.trim`}
                id={`mix-design-constituent-trim-${index}`}
                data-e2e-id={`mix-design-constituent-trim-${index}`}
                type="number"
                value={item.trim || ''}
                disabled={this.constituentShouldBeDisabledForIndicatorError(item)}
                step={this.props.materialStore.isAdmixture(item.constituentItemId) ? 0.01 : 1}
                onChange={e => this.onMixDesignTrimChange(e, index, values, setValues)}
                className="form-control input-sm"/>
            )}
            <ErrorMessage component="span" name={`mixDesign.${index}.trim`} className="help-block" />
          </FormGroup>
        </Col>
        <Col sm={1} xsHidden={true}>
          <FormGroup>
            <ControlLabel data-e2e-id={`lbl-mix-design-by-cement-${index}`}>{t('By/c')}</ControlLabel>
            {!!this.props.materialStore.isAdmixture(item.constituentItemId) && (
              <Checkbox
                inline
                id={`mix-design-constituent-by-cement-${index}`}
                data-e2e-id={`mix-design-constituent-by-cement-${index}`}
                className="mt-checkbox mt-checkbox-outline"
                name={`mixDesign.${index}.byCement`}
                value={item.byCement}
                checked={!!item.byCement}
                onChange={e => this.onByCementChange(e, index, values, setValues)}
              >
                <span />
              </Checkbox>
            )}
          </FormGroup>
        </Col>
        <Col sm={1} xsHidden={true}>
          <FormGroup>
            <ControlLabel data-e2e-id={`lbl-mix-design-mix-${index}`}>{t('Mix')}</ControlLabel>
            <div
              id={`mix-design-constituent-quantity-mix-${index}`}
              data-e2e-id={`mix-design-constituent-quantity-mix-${index}`}
              className="form-data-value">
              {toNumber(this.getFinalQuantity(item, values), this.itemWithoutRounding(item) ? 2 : 1)}
              {isConversionNeeded &&
                <div>{toNumber(convertUnit(this.getFinalQuantity(item, values), unit, globalWaterUnitSymbol), 1)}</div>
              }
            </div>
          </FormGroup>
        </Col>
        <Col sm={1} xs={2}>
          <FormGroup>
            <ControlLabel data-e2e-id={`lbl-mix-design-drop-${index}`}>{t('Drop')}</ControlLabel>
            <div
              id={`mix-design-constituent-quantity-drop-${index}`}
              data-e2e-id={`mix-design-constituent-quantity-drop-${index}`}
              className="form-data-value">
              {toNumber((this.getFinalDropQuantityWithRounding(item, values)) || 0, this.itemWithoutRounding(item) ? 2 : 0)}
              {isConversionNeeded &&
                <div>{toNumber(convertUnit((this.getFinalDropQuantityWithRounding(item, values)), unit, globalWaterUnitSymbol), 0)}</div>
              }
            </div>
          </FormGroup>
        </Col>
        <Col sm={1} xsHidden={true}>
          <FormGroup>
            <ControlLabel data-e2e-id={`lbl-mix-design-batch-${index}`}>{t('Batch')}</ControlLabel>
            <div
              id={`mix-design-constituent-quantity-batch-${index}`}
              data-e2e-id={`mix-design-constituent-quantity-batch-${index}`}
              className="form-data-value">
              {toNumber((this.getFinalBatchQuantityWithRounding(item, values)) || 0, this.itemWithoutRounding(item) ? 2 : 0)}
              {isConversionNeeded &&
                <div>{toNumber(convertUnit((this.getFinalBatchQuantityWithRounding(item, values)), unit, globalWaterUnitSymbol), 0)}</div>
              }
            </div>
          </FormGroup>
        </Col>
        <Col sm={1} xs={2} className="mix-design-row-remove">
          {item.added && (
            <a
              id={`button-remove-constituent-${index}`}
              data-e2e-id={`button-remove-constituent-${index}`}
              className="btn btn-sm btn-default btn-action pull-right"
              title={t('Remove')}
              onClick={() => arrayHelpers.remove(index)
               && this.changeMixDesignOrder(values.mixDesign)}>
              <i className="fa fa-remove"/>
            </a>
          )}
        </Col>
        {this.isMaximumAmountInMixExceeded(item, values) && (
          <Col sm={12}>
            <div className="has-error"><span className="help-block">{t('Maximum amount for ingredient exceeded')}!</span></div>
          </Col>
        )}
        {this.isMaximumAmountInLoadExceeded(item, values, setValues) && (
          <Col sm={12}>
            <div className="has-error"><span className="help-block">{t('Maximum amount in load exceeded')}!</span></div>
            <a className="btn btn-default btn-xs" onClick={() => this.splitTheLoad(values, setValues)}>{t('Split The Load')}</a>
          </Col>
        )}
      </Row>
    );
  };

  renderMixDesign = (values, setFieldValue, errors, touched, setValues) => {
    const { t, settingsStore } = this.props;
    const { configuration } = settingsStore;
    return (
      <FieldArray
        name="mixDesign"
        render={arrayHelpers => (
          <div className="mix-design-form">
            <Panel>
              <Panel.Body>
                <DragDropContext onDragEnd={e => this.onDragEnd(e, values)}>
                  <Droppable droppableId="droppable">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                      >
                        {values.mixDesign.map((item, index) => (

                          <Draggable key={item.id || item.constituentItemId} draggableId={item.id || item.constituentItemId} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={this.getDragItemStyle(
                                  snapshot.isDragging,
                                  provided.draggableProps.style
                                )}
                              >
                                {this.renderMixDesignRow(values, item, index, setFieldValue, arrayHelpers, errors, touched, setValues)}
                                {this.renderMaterialDivisionRow(values, item, index, setValues)}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>

                {configuration.localDispatchOrderChange && (
                  <Row>
                    <Col sm={3}>
                      <a className="btn btn-sm btn-default"
                        title={t('Add constituent')} onClick={() => this.changeMixDesignOrder(values.mixDesign)
                        && arrayHelpers.push({
                          id: shortid.generate(),
                          constituentItemId: '',
                          batchCode: '',
                          batchShortDescription: '',
                          quantity: 0,
                          trim: 0,
                          moisture: 0,
                          quantityNet: 0,
                          byCement: false,
                          unitId: null,
                          order: values.mixDesign.length + 1,
                          added: true,
                        })}>
                        <i className="fa fa-plus"/>
                      </a>
                    </Col>
                  </Row>
                )}
              </Panel.Body>
            </Panel>
          </div>
        )}
      />
    );
  };

  renderForm() {
    const { batchStore, mixStore, dischargeStore, devicesStore, ticketStore, settingsStore, slumpTableStore, t } = this.props;
    const { adjustedMixDesigns, mixOptions } = mixStore;
    const { sequences } = dischargeStore;
    const { mixers } = devicesStore;
    const { configuration } = settingsStore;
    const waterUnit = ticketStore.getWaterUnit();
    const measurementSystem = configuration.measurement;

    return !configuration || !(batchStore.canEmit || batchStore.replayMode || batchStore.canSendRemoteInstruction()) ? '' : (
      <div>
        <Formik
          enableReinitialize
          initialValues={
            this.state.initialValues
          }
          validateOnChange={true}
          validationSchema={this.validationSchema}
          onSubmit={this.onSubmit}
        >{({ handleChange,
            setFieldValue,
            setValues,
            values,
            errors,
            touched,
            isSubmitting,
            resetForm,
            submitForm,
          }) => (
            <Form className={'batch-form'}>
              <BatchFormExtraTools
                currentValues={this.state.initialValues}
                setValues={setValues}
                setInitialValues={(values) => this.setState({ initialValues: values })}
                isSubmitting={isSubmitting}
              />
              <Row>
                <Col sm={3} xs={6}>
                  <FormGroup className={cx({'has-error': errors.quantityToBeBatched && touched.quantityToBeBatched} )}>
                    <ControlLabel data-e2e-id="lbl-batch-quantity">{t('Quantity')}</ControlLabel>
                    <FormControl
                      data-e2e-id="inp-batch-quantity"
                      name="quantityToBeBatched"
                      type="number"
                      min={0}
                      value={values.quantityToBeBatched || ''}
                      onChange={e => this.onQuantityChange(e.target.value, values.mixQuantityInTruck, values, setValues)}
                      bsSize="sm"
                    />
                    <ErrorMessage component="span" name="quantityToBeBatched" className="help-block" />
                  </FormGroup>
                </Col>
                <Col sm={6} xs={12}>
                  <FormGroup className={cx({'has-error': errors.mixId && touched.mixId} )}>
                    <ControlLabel data-e2e-id="lbl-mix">{t('Mix')}</ControlLabel>
                    <Dropdown onChange={(e, data) => this.onMixChange(data.value, values, setValues)}
                      value={values.mixId}
                      data-e2e-id="dpd-mix-search"
                      fluid
                      search={this.onDropdownSearch}
                      selection
                      text={values.mixDescription}
                      options={mixOptions}
                    />
                    <ErrorMessage component="span" name="mixId" className="help-block" />
                  </FormGroup>
                </Col>
                <Col sm={3} xs={12}>
                  <FormGroup className={cx({'has-error': errors.dischargeSequenceId && touched.dischargeSequenceId} )}>
                    <ControlLabel data-e2e-id="lbl-discharge-sequence">{t('Discharge Sequence')}</ControlLabel>
                    <FormControl
                      data-e2e-id="dpd-discharge-sequence"
                      name="dischargeSequenceId"
                      componentClass="select"
                      value={values.dischargeSequenceId || ''}
                      onChange={e => this.onSequenceChange(e.target.value, values, setValues)}
                      bsSize="sm"
                    >
                      <option value=""/>
                      { sequences.map(option => (
                        <option key={option.id} value={option.id}>
                          {option.name ? `${option.name}` : `#${option.id}`}
                        </option>
                      ))}
                    </FormControl>
                    <ErrorMessage component="span" name="dischargeSequenceId" className="help-block" />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col sm={3} xs={6}>
                  <FormGroup>
                    <ControlLabel data-e2e-id="lbl-truck">{t('Truck')}</ControlLabel>
                    <Dropdown onChange={(e, data) => this.onTruckChange(data.value, values, setValues)}
                      value={values.truckId || ''}
                      data-e2e-id="dpd-truck"
                      fluid search selection options={this.truckOptions} />
                  </FormGroup>
                </Col>
                <Col sm={3} xs={6}>
                  <FormGroup>
                    <ControlLabel data-e2e-id="lbl-project">{t('Project')}</ControlLabel>
                    <Dropdown onChange={(e, data) => this.onProjectChange(data.value, values, setValues)}
                      value={values.projectId || ''}
                      data-e2e-id="dpd-project"
                      fluid search selection options={this.projectOptions} />
                  </FormGroup>
                </Col>
                <Col sm={3} xs={6}>
                  <FormGroup>
                    <ControlLabel data-e2e-id="lbl-driver">{t('Driver')}</ControlLabel>
                    <Dropdown onChange={(e, data) => this.onDriverChange(data.value, values, setValues)}
                      value={values.driverId || ''}
                      data-e2e-id="dpd-driver"
                      fluid search selection options={this.driverOptions} />
                  </FormGroup>
                </Col>
                <Col sm={3} xs={6}>
                  <FormGroup className={cx({
                    'has-error': errors.mixQuantityInTruck && touched.mixQuantityInTruck,
                    'quantity-in-truck-warning': values.mixQuantityInTruck > 0,
                  })}>
                    <ControlLabel data-e2e-id="lbl-mix-quantity-in-truck">{t('Quantity in Truck')}</ControlLabel>
                    <FormControl
                      data-e2e-id="inp-mix-quantity-in-truck"
                      name="mixQuantityInTruck"
                      type="number"
                      min={0}
                      value={isNaN(values.mixQuantityInTruck) ? '' : values.mixQuantityInTruck || ''}
                      onChange={e => this.onQuantityChange(values.quantityToBeBatched, e.target.value, values, setValues)}
                      bsSize="sm"
                    />
                    {values.mixQuantityInTruck > 0 && (
                      <div className="warning-message">
                        <strong>⚠️ {t('Note')}:</strong> {values.mixQuantityInTruck} {t('units from previous load')}
                      </div>
                    )}
                    <ErrorMessage component="span" name="mixQuantityInTruck" className="help-block" />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col sm={3} xs={6}>
                  <FormGroup>
                    <ControlLabel data-e2e-id="lbl-mix-in-truck">{t('Mix in Truck')}</ControlLabel>
                    <Dropdown onChange={(e, data) => this.onMixInTruckChange({ ...values, mixInTruck: data.value }, setValues)}
                      value={values.mixInTruck}
                      data-e2e-id="dpd-mix-in-truck"
                      fluid
                      search={this.onDropdownSearch}
                      selection
                      options={mixOptions}
                    />
                    <ErrorMessage component="span" name="mix" className="help-block" />
                  </FormGroup>
                </Col>
                <Col sm={3} xs={6}>
                  <FormGroup>
                    <ControlLabel data-e2e-id="lbl-batch-code">{t('Code')}</ControlLabel>
                    <FormControl
                      data-e2e-id="inp-batch-code"
                      name="code"
                      type="number"
                      min={0}
                      disabled={true}
                      value={values.code || ''}
                      onChange={handleChange}
                      bsSize="sm"
                    />
                  </FormGroup>
                </Col>
                <Col sm={3} xs={6}>
                  <FormGroup>
                    <ControlLabel data-e2e-id="lbl-slump">{t('Slump')}</ControlLabel>
                    <FormControl
                      data-e2e-id="inp-slump"
                      name="slump"
                      type="number"
                      min={0}
                      value={values.slump || ''}
                      onChange={e => {this.onSlumpChange(e.target.value, values, setValues);}}
                      bsSize="sm"
                    />
                  </FormGroup>
                </Col>
                <Col sm={3} xs={6} className="is-water">
                  <ControlLabel data-e2e-id="lbl-water-in-truck">{t('Water In Truck')}</ControlLabel>
                  <FormGroup className={cx({'has-error': errors.waterInTruck && touched.waterInTruck})}>
                    <InputGroup>
                      <FormControl
                        data-e2e-id="inp-water-in-truck"
                        name="waterInTruck"
                        type="number"
                        min={0}
                        value={isNaN(values.waterInTruck) ? '' : values.waterInTruck || ''}
                        onChange={e => {this.onWaterInTruckChange(e.target.value, values, setValues);}}
                        bsSize="sm"
                      />
                      <InputGroup.Addon>
                        {waterUnit || ''}
                      </InputGroup.Addon>
                    </InputGroup>
                    <ErrorMessage component="span" name="waterInTruck" className="help-block" />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col sm={3} xs={6}>
                  <FormGroup className={cx({'has-error': errors.customerId && touched.customerId} )}>
                    <ControlLabel data-e2e-id="lbl-customer">{t('Customer')}</ControlLabel>
                    <Dropdown onChange={(e, data) => this.onCustomerChange(data.value, values, setValues)}
                      value={values.customerId || ''}
                      data-e2e-id="dpd-customer"
                      fluid search selection options={this.customerOptions} />
                    <ErrorMessage component="span" name="customerId" className="help-block" />
                  </FormGroup>
                </Col>
                <Col sm={3} xs={6}>
                  <FormGroup>
                    <ControlLabel data-e2e-id="lbl-usage">{t('Usage')}</ControlLabel>
                    <FormControl
                      data-e2e-id="inp-usage"
                      name="usage"
                      type="text"
                      value={values.usage || ''}
                      disabled={true}
                      bsSize="sm"
                    />
                  </FormGroup>
                </Col>
                <Col sm={3} xs={6}>
                  <ControlLabel data-e2e-id="lbl-ice">{t('Ice')}</ControlLabel>
                  <FormGroup className={cx({ 'has-error': errors.ice && touched.ice })}>
                    <div style={{ display: 'flex' }}>
                      <FormControl
                        data-e2e-id="inp-ice"
                        name="ice"
                        type="number"
                        min={0}
                        value={isNaN(values.ice) ? '' : values.ice}
                        onChange={e => {
                          this.onIceChange(e.target.value, values, setValues);
                        }}
                        bsSize="sm"
                      />
                      <FormControl
                        data-e2e-id="dpd-ice-unit"
                        name="iceUnit"
                        componentClass="select"
                        value={values.iceUnit || IceUnits[Measurement['U.S. Customary']][0].key}
                        onChange={e => {
                          const iceUnit = e.target.value;
                          this.onIceChange(values.ice, values, setValues, iceUnit);
                        }}
                        bsSize="sm"
                      >
                        {IceUnits[measurementSystem]?.map(unit => (
                          <option key={unit.key} value={unit.key}>
                            {unit.label}
                          </option>
                        ))}
                      </FormControl>
                    </div>
                    <ErrorMessage component="span" name="ice" className="help-block" />
                  </FormGroup>
                </Col>
                <Col sm={3} xs={4}>
                  <FormGroup className={cx({'has-error': errors.batchSize && touched.batchSize})}>
                    <ControlLabel data-e2e-id="lbl-batch-size">{t('Batch Size')}</ControlLabel>
                    <FormControl
                      data-e2e-id="inp-batch-size"
                      name="batchSize"
                      type="number"
                      disabled={true}
                      value={values.batchSize || 0}
                      bsSize="sm"
                    />
                    <ErrorMessage component="span" name="batchSize" className="help-block" />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col sm={3} xs={4}>
                  <FormGroup className={cx({'has-error': errors.allDrops && touched.allDrops} )}>
                    <ControlLabel data-e2e-id="lbl-all-drops">#{t('Of Drops')}</ControlLabel>
                    <FormControl
                      data-e2e-id="inp-all-drops"
                      name="allDrops"
                      type="number"
                      min={1}
                      value={values.allDrops || 0}
                      onChange={e => this.onAllDropsChange(e.target.value, values, setValues)}
                      bsSize="sm"
                    />
                    <ErrorMessage component="span" name="allDrops" className="help-block" />
                  </FormGroup>
                </Col>
                <Col sm={3} xs={4}>
                  <FormGroup className={cx({'has-error': errors.dropSize && touched.dropSize} )}>
                    <ControlLabel data-e2e-id="lbl-drop-size">{t('Drop Size')}</ControlLabel>
                    <FormControl
                      data-e2e-id="inp-drop-size"
                      name="dropSize"
                      type="number"
                      disabled={true}
                      value={values.dropSize || 0}
                      bsSize="sm"
                    />
                    <ErrorMessage component="span" name="dropSize" className="help-block" />
                  </FormGroup>
                </Col>
                <Col sm={3} xs={12}>
                  <FormGroup>
                    <Checkbox
                      inline
                      data-e2e-id="chk-print-without-weights"
                      className="mt-checkbox mt-checkbox-outline"
                      name={'printWithoutWeights'}
                      value={values.printWithoutWeights}
                      checked={!!values.printWithoutWeights}
                      onChange={e => {
                        setFieldValue('printWithoutWeights', !!e.target.checked);
                        if (!!e.target.checked) {
                          setFieldValue('printWithWeights', false);
                          setFieldValue('printWithoutWeightsAtEnd', false);
                        }
                      }}
                    >
                      <span />
                      {t('Print Ticket at the Beginning')}{' '}
                    </Checkbox>
                    <Checkbox
                      inline
                      data-e2e-id="chk-print-without-weights-at-end"
                      className="mt-checkbox mt-checkbox-outline"
                      name={'printWithoutWeightsAtEnd'}
                      value={values.printWithoutWeightsAtEnd}
                      checked={!!values.printWithoutWeightsAtEnd}
                      onChange={e => {
                        setFieldValue('printWithoutWeightsAtEnd', !!e.target.checked);
                        if (!!e.target.checked) {
                          setFieldValue('printWithWeights', false);
                          setFieldValue('printWithoutWeights', false);
                        }
                      }}
                    >
                      <span />
                      {t('Print Ticket Without Weights at the End')}{' '}
                    </Checkbox>
                  </FormGroup>
                </Col>
                <Col sm={3} xs={12}>
                  <FormGroup>
                    <Checkbox
                      inline
                      data-e2e-id="chk-print-with-weights"
                      className="mt-checkbox mt-checkbox-outline"
                      name={'printWithWeights'}
                      value={values.printWithWeights}
                      checked={!!values.printWithWeights}
                      onChange={e => {
                        setFieldValue('printWithWeights', !!e.target.checked);
                        if (!!e.target.checked) {
                          setFieldValue('printWithoutWeights', false);
                          setFieldValue('printWithoutWeightsAtEnd', false);
                        }
                      }}
                    >
                      <span />
                      {t('Print Ticket With Weights')}
                    </Checkbox>
                    <Checkbox
                      inline
                      data-e2e-id="chk-print-only-weights"
                      className="mt-checkbox mt-checkbox-outline"
                      name={'printOnlyWeights'}
                      value={values.printOnlyWeights}
                      checked={!!values.printOnlyWeights}
                      onChange={handleChange}
                    >
                      <span />
                      {t('Print Weights')}
                    </Checkbox>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col sm={3} xs={6} className="is-water">
                  <ControlLabel data-e2e-id="lbl-water-adjust">{t('Water Adjust')}</ControlLabel>
                  <FormGroup className={cx({ 'has-error': errors.waterAdjust && touched.waterAdjust })}>
                    <InputGroup>
                      <FormControl
                        data-e2e-id="inp-water-adjust"
                        name="waterAdjust"
                        type="number"
                        value={isNaN(values.waterAdjust) ? '' : values.waterAdjust || ''}
                        onChange={e => this.onWaterAdjustChange(e.target.value, values, setValues)}
                        bsSize="sm"
                      />
                      <InputGroup.Addon>
                        {waterUnit || ''}
                      </InputGroup.Addon>
                    </InputGroup>
                    <ErrorMessage component="span" name="waterAdjust" className="help-block"/>
                  </FormGroup>
                </Col>
                {this.secondWaterExist && (
                  <Col sm={3} xs={6}>
                    <ControlLabel data-e2e-id="lbl-second-water">{t('2nd Water')}</ControlLabel>
                    <FormGroup className={cx({ 'has-error': errors.secondWater && touched.secondWater })}>
                      <InputGroup>
                        <FormControl
                          data-e2e-id="inp-second-water"
                          name="secondWater"
                          type="number"
                          min={-100}
                          max={100}
                          value={isNaN(values.secondWater) ? '' : values.secondWater || ''}
                          onChange={e => this.onSecondWaterChange(e.target.value, values, setValues)}
                          bsSize="sm"
                        />
                        <InputGroup.Addon>
                        %
                        </InputGroup.Addon>
                      </InputGroup>
                      <ErrorMessage component="span" name="secondWater" className="help-block"/>
                    </FormGroup>
                  </Col>
                )}
                {!!values.mixId && !!adjustedMixDesigns.length && (
                  <React.Fragment>
                    <Col sm={3} xs={6}>
                      <FormGroup>
                        <ControlLabel data-e2e-id="lbl-adjusted-mix-design">{t('Adjusted Mix Design')}</ControlLabel>
                        <FormControl
                          data-e2e-id="dpd-adjusted-mix-design"
                          name="adjustedMixDesignId"
                          componentClass="select"
                          value={values.adjustedMixDesignId || ''}
                          onChange={e => this.onAdjustedMixDesignChange(e.target.value, values, setValues)}
                          bsSize="sm"
                        >
                          <option value=""/>
                          {adjustedMixDesigns.map(option => (
                            <option key={option.id} value={option.id}>
                              {option.extraProductItemCode} {option.extraProductItemDescription}
                            </option>
                          ))}
                        </FormControl>
                      </FormGroup>
                    </Col>
                  </React.Fragment>
                )}
                {!!mixers.length && (
                  <Col sm={3} xs={6}>
                    <FormGroup>
                      <ControlLabel data-e2e-id="lbl-mixer">{t('Mixer')}</ControlLabel>
                      <FormControl
                        data-e2e-id="dpd-mixer"
                        name="mixerId"
                        componentClass="select"
                        value={values.mixerId || ''}
                        onChange={e => this.onMixerChange(e.target.value, values, setValues)}
                        bsSize="sm"
                      >
                        <option value="">{t('No mixer')}</option>
                        {mixers.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.id} {option.description}
                          </option>
                        ))}
                      </FormControl>
                    </FormGroup>
                  </Col>
                )}
              </Row>
              <Row>
                {!!values.useBay && (
                  <Col sm={3} xs={6}>
                    <FormGroup>
                      <ControlLabel data-e2e-id="lbl-bay">{t('Bay')}</ControlLabel>
                      <FormControl
                        data-e2e-id="dpd-bay"
                        name="bay"
                        componentClass="select"
                        value={values.bay || 0}
                        onChange={handleChange}
                        bsSize="sm"
                      >
                        <option value="0"/>
                        {Object.keys(BayList).map((option, i) => (
                          <option key={i} value={BayList[option]}>
                            {option}
                          </option>
                        ))}
                      </FormControl>
                    </FormGroup>
                  </Col>
                )}
                {!!values.slumpTableId && (
                  <Col sm={3} xs={6}>
                    <FormGroup>
                      <ControlLabel data-e2e-id="lbl-slump-table">{t('Slump Table')}</ControlLabel>
                      <FormControl
                        data-e2e-id="dpd-slump-table"
                        name="slumpTableId"
                        componentClass="select"
                        value={values.slumpTableId || ''}
                        onChange={handleChange}
                        disabled={true}
                        bsSize="sm"
                      >
                        <option value="0"/>
                        {slumpTableStore.slumpTables.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.code} : {option.description}
                          </option>
                        ))}
                      </FormControl>
                    </FormGroup>
                  </Col>
                )}
              </Row>

              <Row>
                <Col sm={12}>
                  <strong data-e2e-id="lbl-mix-design">{t('Mix Design')}</strong>
                  {this.renderMixDesign(values, setFieldValue, errors, touched, setValues)}
                </Col>
              </Row>
              {!!values.mixId && (
                <Row>
                  <Col sm={3}>
                    <p>
                      <strong data-e2e-id="lbl-wc-ratio">{t('W/C Ratio')}:</strong> <span data-e2e-id="lbl-wc-ratio-value">{getWCRatio(values, this.props.ticketStore, this.props.materialStore)}</span>
                    </p>
                  </Col>
                  {!!values.mix.waterCementRatio && (
                    <Col sm={3}>
                      <p>
                        <strong data-e2e-id="lbl-ctr-wc-ratio">{t('Ctr W/C Ratio')}:</strong> <span data-e2e-id="lbl-ctr-wc-ratio-value">{values.mix.waterCementRatio}</span>
                      </p>
                    </Col>
                  )}
                  <Col sm={3}>
                    <p>
                      <strong data-e2e-id="lbl-total-water">{t('Total Water')}:</strong> <span data-e2e-id="lbl-total-water-value">{getTotalWater(values, this.props.ticketStore, this.props.materialStore)} {waterUnit}</span>
                    </p>
                  </Col>
                  {!!values.mix.waterCementRatio && (
                    <Col sm={3}>
                      <p className={isAllowedWaterExceeded(values, this.props.ticketStore, this.props.materialStore) ? 'text-danger' : ''}>
                        <strong data-e2e-id="lbl-allowed-water">{t('Allowable water')}:</strong> <span data-e2e-id="lbl-allowed-water-value">{toNumber(getAllowedWater(values, this.props.ticketStore, this.props.materialStore) - getTotalWater(values, this.props.ticketStore, this.props.materialStore), 3)} {waterUnit}</span>
                      </p>
                    </Col>
                  )}
                  {/*<Col sm={3}>*/}
                  {/*  <p>*/}
                  {/*    <strong>{t('Yield')}:</strong> <span>{this.calculateYield(values)} m3</span>*/}
                  {/*  </p>*/}
                  {/*</Col>*/}
                </Row>
              )}
              {values.adjustedMixes?.length ? (
                <Row>
                  <Col sm={12}>
                    <strong data-e2e-id="lbl-adjusted-mix-design">{t('Adjusted Mix Design')}</strong>
                    {this.renderAdjustedMixDesign(values)}
                  </Col>
                </Row>
              ) : ''}
              {values.extra && values.extra.length > 0 ? (
                <Row>
                  <Col sm={12}>
                    <strong data-e2e-id="lbl-extra-products">{t('Extra Products')}</strong>
                    {this.renderExtraRow(values)}
                    {<ManualAddList itemsToAdd={values.itemsToBeManuallyAdded} orderMixQuantity={values.quantityToBeBatched}/>}
                  </Col>
                </Row>
              ) : ''}
              <Row>
                <Col sm={12}>
                  {!!this.state.errorsToOverride.length && (
                    <div>
                      {!values.overrideErrors && (
                        <Alert bsStyle="danger">
                          {this.state.errorsToOverride.map((err, i) => (
                            <div key={`${err.name}-${i}`}>{err.error}</div>
                          ))}
                        </Alert>
                      )}
                      <FormGroup>
                        <Checkbox
                          inline
                          className="mt-checkbox mt-checkbox-outline"
                          name={'overrideErrors'}
                          value={values.overrideErrors}
                          checked={!!values.overrideErrors}
                          onChange={(e) => this.onOverrideErrors(e, values, setValues)}
                        >
                          <span />
                          {t('Continue the load anyway ')}
                          {this.state.errorsToOverride.find(v => v.name === 'notVisible') &&
                            t('Change material visibility automatically during the load')
                          }
                        </Checkbox>
                      </FormGroup>
                    </div>
                  )}
                  {!!this.state.error && <Alert bsStyle="danger">{this.state.error}</Alert>}

                  {!!this.state.errors.length && (
                    <Alert bsStyle="danger">
                      {this.state.errors.map((v, i) => (
                        <div key={`${v.name}-${i}`}>
                          {v.error}{' '}
                          {(v.name === 'scaleCapacity' || v.name === 'maximumAmountInLoad') && (
                            <a className="btn btn-default btn-xs" onClick={() => {
                              this.splitTheLoad(values, setValues);
                            }}>{t('Split The Load')}
                            </a>
                          )}
                        </div>
                      ))}
                    </Alert>
                  )}

                  {!!this.state.mixDifferWarnings.length && (
                    <div>
                      <Alert bsStyle="warning">
                        <h5>{t('Mix Design values are different than Mix Design in Truck')}</h5>
                        {this.state.mixDifferWarnings.map((v, i) => (
                          <div key={i}>
                            {v}{' '}
                          </div>
                        ))}
                        <div className="text-right">
                          <Button onClick={() => {
                            this.onAdjustMixDesignClick(values, setValues);
                          }}>
                            {t('Adjust Mix Design')}
                          </Button>
                        </div>
                      </Alert>
                    </div>
                  )}

                  {!!this.state.warnings.length && (
                    <Alert bsStyle="warning">
                      {this.state.warnings.map((v, i) => (
                        <div key={i}>
                          {v}{' '}
                        </div>
                      ))}
                    </Alert>
                  )}

                  {(batchStore.replayMode && !!this.state.initialValues?.verification?.warnings?.length) && (
                    <Alert bsStyle="warning">
                      {this.state.initialValues.verification.warnings.map((v, i) => (
                        <div key={i}>
                          {v}{' '}
                        </div>
                      ))}
                    </Alert>
                  )}

                  {!!this.state.info.length && (
                    <Alert bsStyle="info">
                      {this.state.info.map((v, i) => (
                        <div key={i}>
                          {v}{' '}
                        </div>
                      ))}
                    </Alert>
                  )}

                  {(batchStore.replayMode && !!this.state.initialValues?.verification?.info?.length) && (
                    <Alert bsStyle="info">
                      {this.state.initialValues.verification.info.map((v, i) => (
                        <div key={i}>
                          {v}{' '}
                        </div>
                      ))}
                    </Alert>
                  )}

                </Col>
                {!batchStore.replayMode && (
                  <Col sm={12} className="text-right">
                    <Button
                      data-e2e-id="btn-reset-batch-form"
                      onClick={() => this.onResetForm(resetForm)}>
                      {t('Reset')}
                    </Button>
                    {' '}
                    <LoadingButton
                      bsStyle="default"
                      data-e2e-id="btn-verify-batch-form"
                      onClick={() => this.verify(values)}
                      label={t('Verify')}
                      loadingLabel={t('Verify')}
                      isLoading={isSubmitting}
                    />
                    {values.id && (
                      <React.Fragment>
                        {' '}
                        <LoadingButton
                          bsStyle="info"
                          data-e2e-id="btn-save-batch-form"
                          onClick={() => this.onSaveClick(submitForm)}
                          label={t('Save')}
                          loadingLabel={t('Save')}
                          isLoading={isSubmitting}
                        />
                      </React.Fragment>
                    )}
                    {' '}
                    <LoadingButton
                      bsStyle="default"
                      data-e2e-id="btn-demo-batch-form"
                      onClick={() => this.onDemoClick(submitForm)}
                      label={t('Demo')}
                      loadingLabel={t('Demo')}
                      isLoading={isSubmitting}
                    />
                    {configuration.runMode !== RunMode.Normal && (
                      <React.Fragment>
                        { ' ' }
                        <LoadingButton
                          bsStyle="default"
                          data-e2e-id="btn-test-batch-form"
                          onClick={() => this.onTestClick(submitForm)}
                          label={t('Test')}
                          loadingLabel={t('Test')}
                          isLoading={isSubmitting}
                        />
                      </React.Fragment>
                    )}
                    {!!this.state.enableOfflineBatch && (
                      <React.Fragment>
                        {' '}
                        <LoadingButton
                          bsStyle="default"
                          onClick={() => this.onForceDemoClick(submitForm)}
                          label={t('Force Demo')}
                        />
                        {' '}
                        <LoadingButton
                          bsStyle="primary"
                          onClick={() => this.onForceBatchClick(submitForm)}
                          label={t('Force Batch')}
                        />
                      </React.Fragment>
                    )}
                    {' '}
                    <LoadingButton
                      bsStyle="primary"
                      data-e2e-id="btn-batch-batch-form"
                      onClick={() => this.onBatchClick(submitForm)}
                      label={t('Batch')}
                      loadingLabel={t('Batch')}
                      isLoading={isSubmitting}
                    />
                  </Col>
                )}
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    );
  };

  render() {
    const { batchStore, t } = this.props;
    const { initialValues } = this.state;

    return (
      <SideBar type="batchForm" title={t('Batch edit')} icon={'fa-file-text-o'}>
        {batchStore.replayMode
          ?
          (
            <Tabs id={'batch-form-tabs'} defaultActiveKey={1}>
              <Tab eventKey={1} title={t('Form')}>
                {this.renderForm()}
              </Tab>
              <Tab eventKey={2} title={t('Audit Log')}>
                <BatchFormLogs initialValues={initialValues} />
              </Tab>
            </Tabs>
          )
          :
          (
            this.renderForm()
          )
        }

      </SideBar>
    );
  }
}

export default withTranslation()(inject('batchStore', 'sidebarStore', 'mixStore', 'scheduleStore', 'ticketStore',
  'materialStore', 'settingsStore', 'dischargeStore', 'printStore', 'devicesStore', 'diagnoseStore', 'connectionStore',
  'newAuthStore', 'slumpTableStore', 'manualAddStore')(observer(BatchForm)));
